// Bali, "Blue hour at Tanah Lot": the postcard. Late August, the dry season, a quarter of an hour after sunset, low tide.
// We stand on the clifftop path by the cafe terraces south-east of the rock (about -8.622, 115.088, some 15 m above the
// reef) and look west-north-west (about 300 degrees) at Pura Tanah Lot on its rock island, drawn large and close for
// the picture (about twice the size the path shows): the rounded, overhanging rock of dark volcanic stone, floodlit warm
// from the reef, with sea caves at its wave-cut foot; on its top terrace the five-tiered meru, a three-tiered meru and the
// tall hipped bale with white and yellow umbrellas, trees and bougainvillea over the landward end; the steps cut up the
// landward end with a split gate at their head, and the lamplit cave of the holy spring at its foot. The sun set at about
// 280 degrees, so the last orange band is low on the left; the open sea is to the left, the coast runs on to the right.
// Back to front: the blue-hour sky in smooth bands, the first stars, a big moon, soft clouds lit by the afterglow, two
// kites; the sea holding the afterglow, a jukung sailing home; breakers along the reef edge; the bared reef holding the
// island's light, and the sand strip the tide has left; the cliff wall with lantern posts and the path; on the left a
// coconut palm, the path lamp, banana leaves and a heliconia; on the right our warung under its alang-alang roof (its
// painted sign on the ridge is the billboard), a frangipani, a parked scooter, canang sari and incense on its steps, and
// a penjor arching over the corner; in front the lawn, beanbags round low tables, candles in jars, stepping stones.
// render(t, { crowd }) thins the people (see CROWD); crowd 0 draws nobody at all.
//
// The loop's timeline (seconds; everything else runs all loop long):
//   event                                                      time                     size
//   flying foxes leave the cliff over the bay                   4-20, 206-222            medium
//   the waiter takes coconuts out to the beanbags and back      20-42, 80-102, 140-162,  medium
//                                                               200-222
//   a couple strolls out of the warung along the path, back     30-66, 150-186           medium
//   the janggan dives and climbs again                          36-46, 226-234           medium
//   a big set: three swells build, the third bursts in a white  56-88 (burst 71),        big (the couple points, the
//     plume over the seaward end, foam floods the reef          184-216 (burst 199)      photographer shoots)
//   a cat walks the wall's cap                                  100-150, 178-228         medium
//   a procession with a tall yellow tedung, fruit towers on     94-194                   big (bells at the start, at
//     their heads, canang, a bell and the priest crosses the                             the spring and at the gate)
//     sand in front of the island, is blessed at the spring,
//     climbs the steps and goes in at the gate
//   the temple's lamps come on one by one from the gate         158-172 (out 222-236)    big (chimes)
//   the dog gets up, circles and lies down again                118-128                  medium
//   all loop: a breaker every 16 s, swells and glints on the sea, a jukung sailing across the glow every 120 s, water
//   light on the rock's foot, the breeze in the crowns, incense from the temple, swiftlets round the caves, clouds
//   drifting, stars, the moon's haze, kites, the palm, penjor and banana leaves swaying; the keeper splits a coconut
//   every 40 s, the woman sets a canang every 24 s, the guest sips every 20 s, the photographer lowers his phone every
//   24 s, the kid tugs the kite every 8 s, the fisherman casts every 20 s, walkers come back across the reef, the cat's
//   tail flicks, candles, bulbs, lanterns, fireflies, moths, and frangipani blossoms on the breeze (the city's weather).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, makeHalo, drawHalo, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HZ = 124;                                       // the sea's horizon
const MOON = { x: 302, y: 30, r: 8 };
const GLOW = 50;                                      // where the sun went down: the heart of the afterglow (x)
const TL = { x0: 86, x1: 328, base: 162, cx: 207 };   // Tanah Lot's rock: its widest span and the row it stands on
const MERU = { cx: 167, eave: 78, tip: 35 };          // the five-tiered meru: bottom eave and finial rows
const MERU3 = { cx: 207, eave: 80, tip: 56 };         // the three-tiered meru
const BALE = { x0: 112, x1: 146, eave: 90, peak: 64 };   // the tall hipped bale, left of the meru
const SPRING = { x: 295, y: 152 };                    // the holy spring's cave at the landward foot
const WALL = 186;                                     // top of the cliff wall's cap
const PATH = 196;                                     // the path, from the foot of the wall towards us
const WARUNG = { x0: 358, eave: 140, ridge: 102, counter: 176, floor: 192 };   // on a raised stone plinth
const BB = { x: 396, y: 66, w: 52, h: 20 };           // the warung's painted sign on its ridge: the billboard's face
const LAMP = { x: 62, y: 152 };                       // the path lamp on the left (lantern centre)
const FRANGI = { x: 340, base: 202 };                 // the frangipani's trunk
const PENJOR = { base: [470, 222], ctrl: [484, 2], tip: [404, 28] };

/* ================= Pixel helpers ================= */
const HXC = {};
const HX = h => HXC[h] || (HXC[h] = hex(h));
function mixHex(a, b, t) {
  const A = HX(a), B = HX(b);
  return '#' + [0, 1, 2].map(k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0')).join('');
}
/** an ImageData painter over the scene rows [oy, oy + h) and columns [ox, ox + w), in scene coordinates */
function painter(w, h, ox = 0, oy = 0) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  const put = (i, y, col, a = 255) => {
    i = Math.round(i) - ox; y = Math.round(y) - oy;
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = HX(col), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const fill = (i, y, fw, fh, col) => { for (let b = 0; b < fh; b++) for (let a = 0; a < fw; a++) put(i + a, y + b, col); };
  const alpha = (i, y) => { i -= ox; y -= oy; return (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3]; };
  return { c, x, put, fill, alpha, done: () => { x.putImageData(img, 0, 0); return { c, x: ox, y: oy }; } };
}
const blit = (L, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(L.c, L.x, L.y); ctx.globalAlpha = 1; };
/** an ordered-dithered pick from a dark-to-light palette */
function pick(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** smooth value noise in [0, 1) */
function vnoise(x, y, seed) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  return lerp(lerp(hash(xi, yi, seed), hash(xi + 1, yi, seed), sx), lerp(hash(xi, yi + 1, seed), hash(xi + 1, yi + 1, seed), sx), sy);
}
/** sprite from rows of letters and a colour key ('.' or a missing key: clear) */
function sprite(rows, key, flip = false) {
  const w = Math.max(...rows.map(r => r.length)), p = painter(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, j, col); } });
  return p.done().c;
}
/** piecewise-linear lookup in [[k, v], ...] (k ascending) */
function plin(pts, k) {
  if (k <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) if (k <= pts[i][0]) return lerp(pts[i - 1][1], pts[i][1], (k - pts[i - 1][0]) / (pts[i][0] - pts[i - 1][0]));
  return pts[pts.length - 1][1];
}

/* ================= Sky: blue hour, the last orange band low on the left ================= */
const SKY = ['#0c1236', '#0f163e', '#121a46', '#161f4e', '#1a2456', '#1f295e', '#252e66', '#2b336d', '#323873', '#3a3c78',
  '#433f7c', '#4d427f', '#584480', '#644680', '#71487e', '#7f4a7a', '#8e4c75', '#9d4e6e', '#ad5266', '#bd585e', '#cc6156',
  '#d96c4f', '#e47a4a', '#ec8a47', '#f29c48', '#f6b04f', '#f8c460'];
function skyV(i, y) {
  const u = clamp(y / HZ, 0, 1);
  let v = 0.06 + 0.5 * u ** 1.8 + 0.12 * smooth(0.72, 1, u);
  const g = Math.max(0, 1 - Math.hypot((i - GLOW) / 290, (HZ - y) / 44));
  v += 0.46 * g ** 1.5;
  v += 0.07 * Math.max(0, 1 - Math.hypot((i - MOON.x) / 64, (y - MOON.y) / 48));   // the moon's haze
  return v;
}
function renderSky() {
  const p = painter(W, HZ + 1);
  for (let y = 0; y <= HZ; y++) for (let i = 0; i < W; i++) p.put(i, y, pick(SKY, skyV(i, y), i, y));
  return p.done();
}
const STARS = [];
{
  const r = rng(31);
  while (STARS.length < 70) {
    const x = Math.floor(r() * W), y = Math.floor(r() ** 1.6 * 84);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 40), ph: r(), b: (0.3 + r() * 0.6) * (1 - y / 110), big: r() < 0.08,
      col: r() < 0.75 ? '#e8ecff' : '#fff0d8' });
  }
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const craters = [[-3, -2, 1.8], [2, 2, 1.6], [-1, 4, 1.2], [3, -4, 1.0], [-5, 2, 0.9], [1, -1, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -6 ? '#fffbea' : dx + dy < 3 ? '#fbf1d6' : '#efe2c0';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = dx + dy < 0 ? '#ebdfbe' : '#dccfac';
    if (dd > r - 1.1) col = dx + dy > 2 ? '#d8caa8' : '#f6eccc';
    p.put(i, y, col);
  }
  return p.done().c;
}
// a few thin blue-hour clouds: banks of flat-bellied puffs, darker than the sky they float in, their bellies lit by
// the afterglow; a strip as wide as the frame, so they can drift and wrap
const CLOUD_BANKS = [{ x: 386, y: 62, w: 100, n: 10, seed: 5 }, { x: 66, y: 90, w: 104, n: 9, seed: 7 }, { x: 168, y: 24, w: 76, n: 7, seed: 9 }];
const CLOUD_BANKS2 = [{ x: 296, y: 12, w: 84, n: 6, seed: 21 }, { x: 40, y: 50, w: 70, n: 5, seed: 23 }, { x: 236, y: 82, w: 60, n: 5, seed: 25 }];
function renderClouds(banks = CLOUD_BANKS) {
  const puffs = [];
  for (const b of banks) {
    const r = rng(b.seed);
    for (let k = 0; k < b.n; k++) { const u = r() - 0.5; puffs.push({ x: b.x + u * b.w, y: b.y - 3 - (1 - Math.abs(u) * 2) * 4 - r() * 2, rx: 9 + r() * 11, ry: 3.5 + r() * 3 + (1 - Math.abs(u) * 2) * 3, belly: b.y + 1 }); }
  }
  const D = (i, y) => {                               // [inside, the belly row of the puff it is in]
    let best = -9, belly = 0;
    for (const q of puffs) {
      if (y > q.belly) continue;
      let dx = i - q.x; dx -= Math.round(dx / W) * W;
      const v = 1 - (dx * dx) / (q.rx * q.rx) - ((y - q.y) ** 2) / (q.ry * q.ry);
      if (v > best) { best = v; belly = q.belly; }
    }
    return [best, belly];
  };
  const p = painter(W, HZ);
  for (let y = 0; y < HZ; y++) for (let i = 0; i < W; i++) {
    const [d, belly] = D(i, y), th = (bayer(i, y) - 0.5) * 0.3;
    if (d + th <= 0.04) continue;
    const v = skyV(i, y), glow = Math.max(0, 1 - Math.abs(i - GLOW) / 280), moon = Math.max(0, 1 - Math.hypot(i - MOON.x, y - MOON.y) / 140);
    const under = smooth(belly - 7, belly + 1, y), edge = d < 0.18 ? 0.5 : 1;   // lit from below, soft at the rims
    p.put(i, y, pick(SKY, v + edge * (0.05 + 0.06 * moon + under * (0.15 + 0.26 * glow)), i, y));
  }
  return p.done();
}

/* ================= Kites: a bebean (fish) high in the middle, a janggan with its long tail on the left ================= */
const BEBEAN = { x: 230, y: 12 };
const BEBEAN_ROWS = [
  '.......kk.........', '.....kkRRk........', '..kkkRRRRRkk...kk.', '.kRRRWWWWRRRk.kRk.', 'kRRWkWWWWWWRRkRRk.',
  '.kRRRWWWWRRRk.kRk.', '..kkkRRRRRkk...kk.', '.....kkRRk........', '.......kk.........'];
const KITE_C = { k: '#1a1420', R: '#c8343c', W: '#f0e6d8', Y: '#e8c040', B: '#2a3a7a', G: '#3a8a5a' };
const JANGGAN = { x: 132, y: 26 };
const JANGGAN_ROWS = ['....k....', '...kYk...', '..kYRYk..', 'kkYRRRYkk', '..kYRYk..', '...kYk...', '....k....'];
function drawKites(t) {
  const bx = BEBEAN.x + Math.round(1.5 * wave(t, 8)), by = BEBEAN.y + Math.round(wave(t, 12, 0.3));
  ctx.drawImage(KITE_BEB, bx, by);
  const jx = JANGGAN.x + Math.round(wave(t, 6, 0.2)), jy = JANGGAN.y + Math.round(1.2 * wave(t, 10) + 12 * diveAt(t));
  for (let k = 0; k < 48; k++) {                      // the janggan's long ribbon of a tail, streaming out on the wind
    const s = k / 48, x = jx + 4 - k * 0.85, y = jy + 6 + k * 0.5 + 2.4 * Math.sin(k * 0.26 - TAU * 30 * t / LOOP) * s;
    ctx.globalAlpha = 0.95 - s * 0.5;
    rect(ctx, Math.round(x), Math.round(y), 1, 2, Math.floor(k / 5) % 2 ? '#f0e6d8' : '#d84a3a');
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(KITE_JAN, jx, jy);
}

/* ================= The far coast on the right, the jukung ================= */
const coastTop = x => HZ - 2 - Math.round(7 * smooth(326, 430, x) + 1.5 * vnoise(x / 9, 0, 4));
function renderCoast() {
  const p = painter(W - 318, 14, 318, HZ - 13);
  for (let i = 318; i < W; i++) {
    const top = coastTop(i);
    for (let y = top; y < HZ; y++) p.put(i, y, y === top ? '#3c3768' : y === top + 1 ? '#2a2a56' : '#1e2048');
  }
  return p.done();
}
// a jukung (an outrigger canoe) coming home under its sail, dark against the afterglow on the water
const JUKUNG_ROWS = [
  '.......s.......', '......ss.......', '......sSs......', '.....sSSs......', '.....sSSSs.....', '....sSSSSs.....',
  '....sSSSSSs....', '.......m.......', 'a.aaaaaaaaaa.a.', '.hhhhhhhhhhhhh.', '..hhhhhhhhhhh..', 'f.....a.....f..'];
const JUKUNG_C = { s: '#3a2030', S: '#6a3040', m: '#1a1018', a: '#2a1a24', h: '#1c1220', f: '#3a2a34' };
const JUKUNG = { x: 40, y: 128 };

/* ================= The sea, holding the afterglow ================= */
const SEA = ['#0a1230', '#0e1838', '#132042', '#19284c', '#203056', '#2a365e', '#363c66', '#44426c', '#544870', '#664e70',
  '#7a546e', '#90586a', '#a85e64', '#c06a5c', '#d47a54', '#e48e50', '#eea458'];
const REEF_EDGE = [[0, 180], [30, 172], [64, 163], [96, 155], [150, 152], [330, 150], [400, 147], [480, 146]];
const reefEdge = x => plin(REEF_EDGE, x);
function renderSea() {
  const p = painter(W, 184 - HZ, 0, HZ);
  for (let y = HZ; y < 184; y++) for (let i = 0; i < W; i++) {
    if (y > reefEdge(i) + 3) continue;
    const d = y - HZ;
    let v = skyV(i, HZ - d * 1.15) * 0.8 - 0.02 * d / 30;
    v += 0.04 * (vnoise(i / 26, y / 1.6, 21) - 0.5);                    // long, low swells
    p.put(i, y, y === HZ ? pick(SEA, v + 0.12, i, y) : pick(SEA, v, i, y));
  }
  return p.done();
}

/* ================= The reef flat, bared by the tide: shallow sheets of water holding the sky, rock ledges ================= */
const REEF = ['#0a1028', '#0e1432', '#121a3c', '#182046', '#1e2650', '#262c58', '#30325e', '#3c3864', '#4a3e68', '#5a446a',
  '#6c4a6a', '#7e5068'];
// the low rock ledges that cross the flat (a row, its wobble, its thickness); the sand bar the walkers use runs from
// the island's landward foot towards our cliff on the right
const LEDGES = [{ y: 169, a: 1.6, f: 37, th: 2, seed: 41 }, { y: 176, a: 1.4, f: 29, th: 2, seed: 43 }, { y: 182, a: 1.2, f: 45, th: 2, seed: 47 }];
const ledgeAt = (L, x) => L.y + L.a * Math.sin(x / L.f + L.seed) + (vnoise(x / 11, 0, L.seed) - 0.5) * 2;
// the sand strip the tide has bared along the foot of our cliff, rising to the island's landward foot: the way across
const BAR_TOP = [[0, 179], [120, 178], [220, 177], [262, 174], [288, 168], [304, 164], [330, 164], [360, 170], [420, 178], [480, 180]];
const barTop = x => Math.round(plin(BAR_TOP, x)), inBar = (x, y) => y >= barTop(x) && y < barTop(x) + 6;
function renderReef() {
  const p = painter(W, WALL + 2 - 144, 0, 144);
  for (let y = 144; y < WALL + 2; y++) for (let i = 0; i < W; i++) {
    if (y < reefEdge(i) + 2) continue;
    const d = y - HZ;
    let v = skyV(i, HZ - d * 0.95) * 0.86 + 0.1;                         // a mirror of the sky, a little dimmer
    let pal = REEF;
    let col = null;
    for (const L of LEDGES) {
      if (vnoise(i / 14, 0, L.seed + 5) < 0.3) continue;                 // a ledge breaks off here and there
      const ly = Math.round(ledgeAt(L, i));
      if (y === ly - 1) col = pick(REEF, v + 0.12, i, y);                // its lip catches the sky
      else if (y >= ly && y < ly + L.th) col = y === ly ? '#1a1830' : '#120f22';
    }
    if (inBar(i, y)) {                                                  // the sand: matte, warm where the floodlights reach
      const warm = Math.max(0, ...FLOODS.map(([fx]) => 1 - Math.hypot((i - fx) / 70, (y - 164) / 26)));
      col = pick(['#221e30', '#2c2638', '#383040', '#463a46', '#58484c', '#6e5650'], 0.3 + 0.62 * warm + 0.1 * (vnoise(i / 6, y, 49) - 0.5), i, y);
      if (y === barTop(i)) col = pick(REEF, v + 0.16, i, y);                // a bright wet rim
    }
    p.put(i, y, col || pick(pal, v, i, y));
  }
  return p.done();
}

/* ================= Pura Tanah Lot ================= */
// Measured on photos from this side (Commons "Pura Tanah Lot, Bali, Indonesia, 20220827 1029 1193", from the south-east
// cliff, and "... 0958 1114", from the east): the island's face is about 0.25 to 0.32 of its width high, the crown (rock
// top to the meru's finial) about half the whole silhouette, the meru's five tiers 1.17 times as tall as the bottom one
// is wide and the top tier 0.65 as wide. Drawn: face 56 of 230 px (0.24; the main mass 0.27), crown 66 of 128 (0.52),
// tiers 44 of 36 (1.22), top tier 23 of 36 (0.64). The rock is rounded: its lip bulges over the face, highest in the
// middle and rolling down at both ends, and the waves have cut a notch at its foot. Floodlights on the reef light it
// warm from below, so it is the brightest, warmest thing in the frame.
// Tanah Lot's rock is dark volcanic stone, grey-black: rounded masses bulging over a foot the waves have cut back,
// irregular on top under the temple and the trees. Floodlights on the reef light it from below, so the warmth is all
// in the light: the undersides of the bulges glow, their tops stay cool grey under the sky, creases stay black.
const STONE = ['#09080d', '#0f0e14', '#15141b', '#1c1a22', '#24212b', '#2d2934', '#37323e', '#433c49', '#504754'];
const LIT = ['#24140f', '#341b13', '#4a2516', '#62311b', '#7c3f20', '#984f25', '#b2602c', '#c87434', '#da8a40', '#e8a24e',
  '#f2bc64', '#fad482'];
// rounded masses of every size, x, y, rx, ry, and how far forward each stands (the face's, then the domed crown
// under the temple: highest in the middle, rolling down to the seaward ledge and the landward shoulder)
const LOBES = [[96, 138, 13, 24, 0.1], [104, 121, 12, 8, 0], [126, 137, 22, 21, 0], [150, 124, 26, 17, 0.12], [160, 147, 30, 13, 0.05],
  [196, 121, 21, 15, 0], [214, 141, 26, 19, 0.1], [244, 125, 20, 21, 0], [270, 141, 24, 17, 0.08], [283, 120, 18, 13, 0.04],
  [306, 135, 16, 22, 0], [320, 126, 10, 14, 0], [178, 133, 11, 9, 0.16], [232, 151, 12, 8, 0.14], [116, 152, 10, 7, 0.1],
  [118, 110, 12, 7, -0.05], [146, 104, 22, 8, -0.05], [184, 99, 26, 9, -0.05], [224, 98, 28, 10, -0.05], [262, 103, 24, 9, -0.05],
  [292, 111, 18, 9, -0.05], [311, 118, 12, 8, -0.05]];
const FOOT_L = [[146, 92], [151, 94], [155, 98], [158, 97], [162, 93]], FOOT_R = [[146, 322], [151, 320], [155, 316], [158, 318], [162, 322]];
const FLOODS = [[128, 170, 54], [206, 172, 60], [282, 170, 52]];   // floodlights on the reef: x, y, reach
function lobeField(x, y) {                            // the two strongest lobes at a pixel: [m1, lobe1, m2]
  let m1 = -9, m2 = -9, L1 = null;
  for (const L of LOBES) {
    const [cx, cy, rx, ry, z] = L, e = (vnoise(x / 4, y / 4, 88) - 0.5) * 0.18;
    const d = 1 - Math.hypot((x - cx) / rx, (y - cy) / ry) + e;
    if (d <= 0) continue;
    const m = d + z;
    if (m > m1) { m2 = m1; m1 = m; L1 = L; } else if (m > m2) m2 = m;
  }
  return [m1, L1, m2];
}
const coreTop = x => 104 + 11 * ((x - 207) / 116) ** 2;   // the solid body behind the masses
const RM = new Uint8Array(W * H), ROCK_TOPS = new Int16Array(W).fill(999);
for (let y = 90; y <= TL.base; y++) for (let x = TL.x0 - 4; x <= TL.x1 + 4; x++) {
  const fl = Math.round(plin(FOOT_L, y) + 1.2 * (vnoise(y / 3, 1, 76) - 0.5)), fr = Math.round(plin(FOOT_R, y) + 1.2 * (vnoise(y / 3, 2, 76) - 0.5));
  const inside = y >= 146 ? x >= fl && x <= fr : lobeField(x, y)[1] !== null || (x >= 93 && x <= 318 && y >= coreTop(x));
  if (inside) { RM[y * W + x] = 1; if (y < ROCK_TOPS[x]) ROCK_TOPS[x] = y; }
}
const inRock = (x, y) => x >= 0 && x < W && y >= 0 && y < H && RM[y * W + x] === 1;
const dome = x => ROCK_TOPS[clamp(Math.round(x), 0, W - 1)];
function rockPixel(x, y) {                            // the stone, lit
  let pool = 0;
  for (const [fx, fy, rr] of FLOODS) pool = Math.max(pool, 1 - Math.hypot((x - fx) / rr, (y - fy) / (rr * 1.25)));
  pool = Math.max(0, pool) ** 0.8;
  let nx = 0, ny = 0.25, crease = 0, recess = 1;
  if (!lobeField(x, y)[1] && y < 146) recess = 0.55;                            // set back between the masses
  if (lobeField(x, y)[1]) {
    const [m1, L, m2] = lobeField(x, y), [cx, cy, rx, ry] = L;
    nx = (x - cx) / rx; ny = (y - cy) / ry;
    if (m1 - m2 < 0.07) crease = 1 - (m1 - m2) / 0.07;                           // where two masses meet
  }
  const tex = vnoise(x / 3, y / 2.2, 86) - 0.5, tex2 = vnoise(x / 9, y / 7, 87) - 0.5, pocket = vnoise(x / 3.2, y / 2.6, 89);
  let warm = (0.52 + 0.36 * clamp(ny, -1, 1) - 0.1 * Math.abs(nx)) * (0.16 + 1.05 * pool) + 0.14 * tex + 0.1 * tex2;
  let cool = 0.3 + 0.3 * clamp(-ny, 0, 1) + 0.22 * tex + 0.12 * tex2;
  warm *= recess;
  if (pocket > 0.79) { warm -= 0.3 * (pocket - 0.79) / 0.21 + 0.08; cool -= 0.12; }   // weathered pockets in the tuff
  if (y >= TL.base - 2) { warm *= 0.35; cool -= 0.15; } else if (y >= TL.base - 5) warm *= 0.7;   // the wet notch
  warm -= crease * 0.35; cool -= crease * 0.25;
  return warm > 0.2 + bayer(x, y) * 0.06 ? pick(LIT, warm, x, y) : pick(STONE, cool, x, y);
}
const CAVES = [{ cx: 110, w: 8, h: 10 }, { cx: 144, w: 10, h: 13 }, { cx: 185, w: 7, h: 9 }, { cx: 223, w: 11, h: 14 }, { cx: 254, w: 6, h: 8 }];
const SHRUBS = [[128, 126, 3], [175, 119, 4], [249, 131, 3], [114, 141, 3], [210, 137, 2], [284, 121, 3], [97, 125, 2]];
const STEPS = { x0: 304, y0: 160, x1: 322, y1: 114, n: 15 };
const TLO = 30;                                       // the island's canvas starts at this row
function renderIsland() {
  const p = painter(W, TL.base + 4 - TLO, 0, TLO), P = p.put, F = p.fill;
  // the rock
  for (let y = 90; y <= TL.base; y++) for (let x = TL.x0 - 4; x <= TL.x1 + 4; x++) {
    if (!inRock(x, y)) continue;
    let col = rockPixel(x, y);
    if (!inRock(x - 1, y) && y < 146) col = y < 130 ? '#6a4a5e' : '#3e2a36';   // the seaward edge, rimmed by the dusk
    else if (!inRock(x + 1, y)) col = '#0a080c';
    for (const c of CAVES) {                                                  // caves the sea has cut into the foot
      const u = (x - c.cx) / (c.w / 2), ay = TL.base - c.h + u * u * 4;
      if (Math.abs(u) < 1 && y > ay) col = y < ay + 1.3 ? '#e09a58' : u > 0.4 ? '#140c10' : '#0a0609';
    }
    P(x, y, col);
  }
  // plants: a fringe hanging over the lip, tufts on the ledges of the face, lit from below
  for (let x = TL.x0; x <= TL.x1; x++) {
    const t0 = dome(x), n = Math.round(1 + 3.2 * vnoise(x / 3, 5, 75));
    if (!inRock(x, t0)) continue;
    for (let k = 0; k < n; k++) P(x, t0 + k, k === n - 1 ? '#5a6230' : k === 0 ? '#2a3a26' : '#1c2a1c');
  }
  for (const [sx, sy, sr] of SHRUBS) for (let x = sx - sr; x <= sx + sr; x++) {        // tufts on the ledges, trailing down
    const n = 1 + Math.round((sr + 1) * (1 - Math.abs(x - sx) / (sr + 1)) * (0.5 + vnoise(x / 1.5, 3, 84)));
    for (let k = 0; k < n; k++) if (inRock(x, sy + k)) P(x, sy + k, k === n - 1 ? '#56642e' : k === 0 ? '#1e2e1c' : '#2a3e24');
  }
  // the holy spring: a cave at the landward foot, lamplit inside
  for (let y = 142; y <= TL.base; y++) for (let x = SPRING.x - 8; x <= SPRING.x + 8; x++) {
    const u = (x - SPRING.x) / 8, ay = 145 + u * u * 6;
    if (y < ay || !inRock(x, y)) continue;
    const d = Math.hypot((x - SPRING.x) / 7, (y - 158) / 12);
    P(x, y, y < ay + 1 ? '#ffd090' : pick(['#2a1410', '#4a2414', '#723818', '#a0561e', '#d07e2a', '#f2a440'], 1 - d, x, y));
  }
  // the steps cut up the landward end, their white-painted rail
  for (let k = 0; k <= STEPS.n; k++) {
    const x = Math.round(lerp(STEPS.x0, STEPS.x1, k / STEPS.n)), y = Math.round(lerp(STEPS.y0, STEPS.y1, k / STEPS.n));
    F(x - 3, y, 6, 1, '#f0c890'); F(x - 3, y + 1, 6, 1, '#6a3a26'); F(x - 3, y + 2, 6, 1, '#a0602e');
    if (k % 2 === 0) { P(x - 4, y - 1, '#e8e4e0'); P(x - 4, y - 2, '#e8e4e0'); }
    P(x - 4, y - 3, '#d8d0d0');
  }
  // the split gate at the head of the steps (candi bentar): two stepped halves
  for (const [gx, s] of [[315, -1], [324, 1]]) for (let y = 98; y < 114; y++) {
    const w = y < 100 ? 1 : y < 104 ? 2 : 3;
    for (let k = 0; k < w; k++) P(gx + s * k, y, k === 0 ? (s < 0 ? '#6a4a40' : '#3a2428') : '#4a3036');
  }
  // the terrace wall along the crown and the low wall round the seaward ledge: brick courses, a stone cap
  for (let x = 90; x <= 238; x++) {                                          // (the trees cover the rest)
    if (x > 104 && x < 110) continue;
    const t0 = dome(x), hgt = x < 106 ? 3 : 5;
    for (let k = 1; k <= hgt; k++) P(x, t0 - k, k === hgt ? '#a08a8a' : k === hgt - 1 ? '#5a4448' : ((x + (k & 1) * 2) % 5 ? '#6a3a32' : '#3a2226'));
  }
  { const sb = dome(95) - 3; F(93, sb - 4, 4, 4, '#4a3a40'); P(93, sb - 4, '#8a7270'); F(92, sb - 6, 6, 2, '#1a1216'); F(93, sb - 8, 4, 2, '#1a1216'); P(94, sb - 9, '#1a1216'); P(94, sb - 3, '#f0c040'); P(95, sb - 3, '#e84a5a'); }   // a shrine on the ledge
  renderBale(p); renderMeru(p, MERU.cx, MERU.eave, [36, 32, 28, 25, 23], 6); renderTrees(p); renderMeru(p, MERU3.cx, MERU3.eave, [24, 21, 18], 6);
  for (const [ux, top, col] of [[100, 100, '#ece6e0'], [150, 84, '#e8c040'], [190, 84, '#ece6e0'], [231, 88, '#e8c040'], [300, 94, '#ece6e0']]) umbrella(p, ux, top, col);
  // boulders at both feet, wet and lit
  for (const [bx, by, br] of [[84, 160, 4], [91, 162, 3], [98, 163, 2], [324, 161, 4], [332, 163, 3], [317, 163, 2]]) {
    for (let y = by - br; y <= by + 1; y++) for (let x = bx - br - 1; x <= bx + br + 1; x++) {
      const d = Math.hypot((x - bx) / (br + 1), (y - by) / br);
      if (d < 1) P(x, y, d > 0.75 && y < by ? '#d08a52' : y > by - 1 ? '#1a0c10' : '#4a2420');
    }
  }
  return p.done();
}
// a ceremonial umbrella (tedung) on its tall pole
function umbrella(p, x, top, col) {
  const sh = mixHex(col, '#2a1a30', 0.45);
  for (let y = top + 3; y <= dome(x) - (x < 106 ? 3 : 5); y++) p.put(x, y, '#3a2a24');
  p.fill(x - 1, top, 3, 1, col); p.fill(x - 3, top + 1, 7, 1, col); p.put(x + 3, top + 1, sh); p.put(x + 2, top + 1, sh);
  for (let k = -3; k <= 3; k += 2) p.put(x + k, top + 2, mixHex(col, '#ffffff', 0.3));
}
const THATCH = ['#08060a', '#0e0a0e', '#151014', '#1d161a', '#261c1e', '#34241e', '#4a3020', '#6a4426', '#8a5a2e'];
// a meru: tiers of black palm-fibre (ijuk) thatch, each a little narrower, its eave lit from below, a short neck of
// the shrine between them; the stone body under the bottom tier, a gilded door; the finial on top
function renderMeru(p, cx, eave, widths, slope) {
  const P = p.put, F = p.fill;
  const base = dome(cx) - 3;
  for (let y = eave + 1; y < base; y++) for (let dx = -5; dx <= 5; dx++) {                 // the body
    let col = dx === -5 ? '#7a5a52' : dx === 5 ? '#1a1014' : (y - eave) % 5 === 0 ? '#2a1c20' : '#3a2a2c';
    if (Math.abs(dx) <= 2 && y > eave + 7) col = Math.abs(dx) === 2 || y === eave + 8 ? '#b08a3a' : '#4a2a1a';   // a carved door, gilt frame
    P(cx + dx, y, col);
  }
  widths.forEach((w, k) => {
    const y0 = eave - k * 8, hw = w / 2;
    for (let j = 0; j < slope; j++) {                                                   // the roof, eave up
      const y = y0 - j, half = Math.round(hw - j * (j < 2 ? 0.5 : 1.25));
      for (let dx = -half; dx <= half; dx++) {
        let v = 0.18 + 0.1 * ((dx + half) % 2) + (j === 0 ? 0.6 : j === 1 ? 0.25 : 0) - 0.1 * (dx / half);
        if (j === 0 && Math.abs(dx) === half) v = 0.7;                                   // the flared corners catch the light
        P(cx + dx, y, pick(THATCH, v, cx + dx, y));
      }
    }
    P(cx - Math.round(hw), y0 + 1, THATCH[1]); P(cx + Math.round(hw), y0 + 1, THATCH[1]);   // the corners turn down
    F(cx - 3, y0 - slope, 7, 2, '#2a1c1c'); P(cx - 3, y0 - slope, '#5a3a2a');           // the neck to the next tier
  });
  const top = eave - (widths.length - 1) * 8 - slope;                                   // the finial
  F(cx - 2, top - 1, 5, 2, '#1d161a'); F(cx - 1, top - 3, 3, 2, '#261c1e'); P(cx, top - 4, '#8a6a3a'); P(cx, top - 5, '#d8a850');
}
function renderBale(p) {
  const P = p.put, F = p.fill, { x0, x1, eave, peak } = BALE, cx = (x0 + x1) / 2;
  for (let y = eave + 1; y <= dome(cx) - 3; y++) for (let x = x0 + 4; x <= x1 - 4; x++) {   // under the roof: lamplit, its posts
    const post = (x - x0 - 4) % 8 === 0;
    P(x, y, post ? '#1a1014' : y === eave + 1 ? '#3a2014' : pick(['#4a2a18', '#7a4a22', '#b07032', '#e0a04a'], 0.9 - Math.abs(x - cx) / 14, x, y));
  }
  for (let y = peak; y <= eave; y++) {
    const u = (y - peak) / (eave - peak), half = Math.round(2 + u * ((x1 - x0) / 2 - 2));
    for (let dx = -half; dx <= half; dx++) {
      let v = 0.2 + 0.1 * ((dx + half + (y >> 1)) % 2) - 0.12 * dx / half;
      if (y >= eave - 1) v = y === eave ? 0.78 : 0.45;                                   // the thick eave, lit from inside
      P(Math.round(cx + dx), y, pick(THATCH, v, cx + dx, y));
    }
  }
  F(Math.round(cx) - 1, peak - 2, 3, 2, THATCH[2]); P(Math.round(cx), peak - 3, '#d8a850');
}
// the trees over the landward end: crowns lit warm from below by the floodlights, rimmed cool by the moon on top;
// bougainvillea spilling over the rock's landward shoulder
const LEAF = ['#070e0c', '#0b1511', '#101d16', '#16261b', '#1d3021', '#273b27', '#33462c', '#455031', '#5c5934', '#786637', '#94733a'];
const BOUG = ['#5a1440', '#7a1e54', '#a02c6a', '#c84686', '#e86aa6'];
const LUMPS = [[227, 80, 10], [242, 68, 12], [260, 62, 13], [280, 65, 12], [298, 74, 11], [312, 85, 10], [321, 97, 8],
  [234, 91, 11], [254, 83, 12], [274, 85, 12], [294, 92, 11], [220, 96, 8], [305, 104, 9],
  [151, 89, 8], [184, 89, 8], [198, 91, 6], [216, 90, 6]];      // and the courtyard trees round the merus
const LEAF_PX = [];                                   // the crowns' pixels and their shade, for the breeze's stir
function renderTrees(p) {
  const field = (x, y) => {
    let m = -9, L = null;
    for (const l of LUMPS) {
      const [cx, cy, r] = l, e = (vnoise(x / 3, y / 3, 81) - 0.5) * 3.2;
      const d = 1 - Math.hypot(x - cx, (y - cy) * 1.08) / (r + e);
      if (d > m) { m = d; L = l; }
    }
    return { m, L };
  };
  const inLeaf = (x, y) => field(x, y).m > 0;
  for (let y = 48; y <= 124; y++) for (let x = 118; x <= 334; x++) {
    const { m, L } = field(x, y);
    if (m <= 0 || y > dome(Math.min(x, TL.x1)) + (x > 303 ? 22 : x > 238 ? 2 : -5)) continue;   // behind the wall, over the lip and the shoulder
    const [cx, cy, r] = L, clump = vnoise(x / 2.6, y / 2.4, 82);
    let v = 0.16 + 0.16 * clamp((y - cy) / r, -1, 1) + 0.26 * (clump - 0.5) + 0.42 * smooth(86, 108, y);
    if (!inLeaf(x, y - 1)) v = 0.4;                                                      // the sky's rim on top
    let col = pick(LEAF, v, x, y);
    if (!inLeaf(x, y - 1) && x > cx) col = '#3a5458';                                    // the moon's side
    const boug = x > 296 && y > 84 && clump > 0.55 && hash(x, y, 83) < 0.75;
    if (boug) col = pick(BOUG, (clump - 0.55) * 4 + smooth(84, 120, y) * 0.5, x, y);
    p.put(x, y, col);
    if (inLeaf(x, y - 1) && hash(x, y, 84) < 0.34) LEAF_PX.push([x, y, boug ? pick(BOUG, (clump - 0.3) * 4, x, y) : pick(LEAF, v + 0.24, x, y)]);
  }
}

/* ================= Our clifftop: the coconut palm, the lamp, the cliff wall, the path ================= */
// the lights that pool on the wall and the paving: x, y, reach, colour, strength
const CANDLES = [[183, 229], [282, 236], [112, 250], [236, 254]];        // candles in jars on the lawn and its low tables
const POOLS = [[LAMP.x, 204, 58, '#ffb060', 0.6], [86, 190, 26, '#ffb060', 0.4], [292, 190, 26, '#ffb060', 0.4],
  [420, 204, 80, '#ffaa58', 0.62], [366, 204, 30, '#ffb868', 0.45], ...CANDLES.map(([x, y]) => [x, y + 3, 26, '#ffa850', 0.45])];
function lit(i, y, col) {
  for (const [cx, cy, r, lc, s] of POOLS) {
    const d = Math.hypot((i - cx) / r, (y - cy) / (r * 0.42));
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * s * 5 + bayer(i, y)) / 5;
    if (q > 0) col = mixHex(col, lc, Math.min(0.55, q * 0.65));
  }
  return col;
}
const POSTS = [86, 292];                              // stone posts on the cliff wall, a little lantern on each
function renderWall() {
  const p = painter(W, PATH - 166, 0, 166);
  for (let y = WALL; y < PATH; y++) for (let i = 0; i < W; i++) {
    const k = y - WALL;
    let col;
    if (k === 0) col = '#a89aa2';                                                   // the stone cap
    else if (k === 1) col = '#6a5c6a';
    else if (k <= 3) col = (i + k * 3) % 8 === 0 ? '#362c3c' : '#544858';           // a course of grey paras
    else if (k <= 7) {                                                              // red brick, two courses
      const course = k <= 5 ? 0 : 1;
      col = (i + course * 3) % 6 === 0 ? '#3a2224' : k % 2 ? '#6e3a32' : '#7e4638';
    } else col = '#2e2834';                                                         // its foot, in shadow
    p.put(i, y, lit(i, y, col));
  }
  for (const x0 of POSTS) {
    for (let y = 179; y < PATH; y++) for (let i = x0 - 3; i <= x0 + 3; i++) {
      const e = i - x0;
      let col = e === -3 ? '#8a7e8a' : e === 3 ? '#2a2230' : y % 4 === 0 ? '#463c4c' : '#5a4e5e';
      if (y <= 180) col = e === 3 ? '#5a4e5e' : '#b0a2aa';
      p.put(i, y, lit(i, y, col));
    }
    p.fill(x0 - 2, 172, 5, 7, '#2a2026'); p.fill(x0 - 1, 174, 3, 3, '#ffd890'); p.put(x0, 175, '#fff2c8');   // the lantern
    p.fill(x0 - 3, 171, 7, 1, '#3a2c2e'); p.fill(x0 - 2, 170, 5, 1, '#3a2c2e'); p.put(x0, 169, '#3a2c2e');
  }
  return p.done();
}
function renderPath() {
  const p = painter(W, 216 - PATH, 0, PATH), r = rng(91);
  p.fill(0, PATH, W, 216 - PATH, '#120e17');
  const rows = [[196, 2], [198, 3], [201, 3], [204, 4], [208, 3], [211, 3]];
  rows.forEach(([y0, rh], ri) => {
    let cx = -Math.floor(r() * 8);
    while (cx < W) {
      const w = 8 + ri * 2 + Math.floor(r() * 6), tone = r();
      for (let y = y0; y < y0 + rh - 1; y++) for (let i = cx + 1; i < cx + w; i++) {
        if (i < 0 || i >= W) continue;
        let col = tone < 0.3 ? '#24202c' : tone < 0.8 ? '#2a2532' : '#312b38';
        if (y === y0) col = '#3c3546';                                              // the stone's top edge
        p.put(i, y, lit(i, y, col));
      }
      cx += w;
    }
  });
  for (let i = 0; i < W; i++) { p.put(i, 214, lit(i, 214, '#5a5060')); p.put(i, 215, '#2a2430'); }   // the kerb
  return p.done();
}
// the clifftop lawn in front of the path: beanbags round low tables, candles in jars; a banana plant and heliconia
// at the foot of the palm
const GRASS = ['#060c0a', '#09110d', '#0d1611', '#111c15', '#152219', '#1a281c', '#203020', '#283824'];
const BEANBAGS = [{ x: 160, y: 238, col: '#c8503e' }, { x: 204, y: 236, col: '#e0a848' }, { x: 306, y: 244, col: '#3a98a4' }, { x: 262, y: 246, col: '#8a4aa8' }];
const TABLES = [[183, 232], [282, 240]];
const STONES = [[356, 219, 6], [342, 223, 7], [325, 227, 7], [305, 231, 8], [282, 233, 8], [258, 235, 8], [233, 236, 9], [207, 236, 8]];
const lawnLight = (i, y) => Math.max(...CANDLES.map(([cx, cy]) => 1 - Math.hypot(i - cx, (y - cy) * 1.6) / 30),
  1 - Math.hypot(i - 420, (y - 214) * 1.4) / 80, 1 - Math.hypot(i - LAMP.x, (y - 214) * 1.4) / 56, 0);
function renderGarden() {
  const p = painter(W, H - 216, 0, 216);
  for (let y = 216; y < H; y++) for (let i = 0; i < W; i++) {
    const L = lawnLight(i, y);
    let col = lit(i, y, pick(GRASS, 0.42 - (y - 216) / 120 + 0.2 * (vnoise(i / 12, y / 3, 121) - 0.5) + 0.25 * L, i, y));
    if (L > 0.08 && hash(i, y, 141) < 0.16 * L) col = mixHex('#2e3e22', '#c88a40', Math.min(0.5, L * 0.6));   // blades catching the light
    p.put(i, y, col);
    if (L > 0.08 && hash(i, y, 141) < 0.16 * L && y > 216) p.put(i, y - 1, mixHex('#24321c', '#9a6a34', Math.min(0.45, L * 0.5)));
  }
  for (const [sx, sy, rx] of STONES) for (let y = sy - 2; y <= sy + 2; y++) for (let x = sx - rx; x <= sx + rx; x++) {   // stepping stones
    const d = Math.hypot((x - sx) / rx, (y - sy) / 2.4);
    if (d >= 1) continue;
    const L = lawnLight(x, y);
    p.put(x, y, y === sy + 2 || d > 0.85 && y > sy ? '#16141c' : y < sy - 0.5 ? mixHex('#5a5262', '#e0a060', L * 0.5) : mixHex('#3e3846', '#a87040', L * 0.5));
  }
  for (const b of BEANBAGS) {
    const ramp = [mixHex(b.col, NIGHT, 0.72), mixHex(b.col, NIGHT, 0.5), mixHex(b.col, NIGHT, 0.28), b.col, mixHex(b.col, '#ffe0b0', 0.3)];
    for (let y = b.y - 9; y <= b.y; y++) for (let x = b.x - 9; x <= b.x + 9; x++) {
      const dx = (x - b.x) / 9, dy = (y - b.y + 3.5) / 4.5, top = (y - b.y + 6) / 3.5;
      if (dx * dx + dy * dy > 1 && dx * dx * 2.2 + top * top > 1) continue;
      const c = CANDLES.reduce((m, [cx, cy]) => Math.max(m, 1 - Math.hypot(x - cx, y - cy) / 40), 0);
      p.put(x, y, pick(ramp, 0.25 + 0.5 * c - 0.25 * dy + 0.12 * (-dx), x, y));
    }
    p.fill(b.x - 7, b.y + 1, 15, 1, '#050806');
  }
  for (const [tx, ty] of TABLES) { p.fill(tx - 7, ty, 15, 2, '#6a4428'); p.fill(tx - 7, ty, 15, 1, '#b07a44'); p.fill(tx - 6, ty + 2, 1, 4, '#2a1a10'); p.fill(tx + 6, ty + 2, 1, 4, '#2a1a10'); }
  for (const [cx, cy] of CANDLES) { p.fill(cx - 1, cy - 3, 3, 4, '#c8b8a0'); p.put(cx, cy - 2, '#ffe0a0'); p.put(cx, cy - 3, '#fff8e0'); }
  return p.done();
}
// big banana leaves and a heliconia rising from the bottom-left corner, lit along one edge by the path lamp
const BANANA = [[[10, 272], [0, 238], [6, 208]], [[14, 272], [26, 240], [48, 222]], [[18, 272], [40, 256], [66, 252]], [[6, 272], [-2, 252], [-12, 236]]];
function renderPlants(sw = 0) {
  const p = painter(80, 80, 0, 192);
  BANANA.forEach(([a, c0, b0], li) => {
    const c = [c0[0] + sw * 1.2, c0[1]], b = [b0[0] + sw * (2.4 - li * 0.3), b0[1] + Math.abs(sw) * 0.6];
    for (let k = 0, n = 160; k <= n; k++) {
      const s = k / n, u = 1 - s, x = u * u * a[0] + 2 * u * s * c[0] + s * s * b[0], y = u * u * a[1] + 2 * u * s * c[1] + s * s * b[1];
      const dx = 2 * u * (c[0] - a[0]) + 2 * s * (b[0] - c[0]), dy = 2 * u * (c[1] - a[1]) + 2 * s * (b[1] - c[1]), l = Math.hypot(dx, dy);
      const nx = -dy / l, ny = dx / l, wd = s < 0.22 ? 1 : 8 * Math.sin(Math.PI * Math.min(1, (s - 0.18) / 0.82)) ** 0.7;
      for (let q = -wd; q <= wd; q += 0.5) {
        if (Math.abs(q) > 1.5 && hash(Math.round(s * 30), li, 131) < 0.18) continue;     // the leaf's splits
        const X = x + nx * q, Y = y + ny * q, edge = Math.abs(q) > wd - 1;
        p.put(X, Y, Math.abs(q) < 0.6 ? '#4a6234' : edge ? (q * nx > 0 ? '#3e5a2c' : '#0a1810') : q * nx > 0 ? '#1c3420' : '#122818');
      }
    }
  });
  // a heliconia: a stem arching out of the leaves, its flower hanging in a zigzag of red, yellow-tipped bracts
  for (let k = 0; k <= 12; k++) p.put(Math.round(30 + k * 0.8), Math.round(214 - 6 * Math.sin(Math.PI * k / 12)), '#3a6a2a');
  const BRACT = ['yRR..', '.RRRg', '..rRg'], BC = { y: '#f2c040', R: '#d8342c', r: '#9a1e1e', g: '#3a6a2a' };
  for (let k = 0; k < 7; k++) {
    const left = (k & 1) === 0, by = 215 + k * 3, bx = left ? 34 : 41, w = k < 2 ? 4 : 5;
    p.put(40, by + 1, '#3a6a2a'); p.put(40, by + 2, '#3a6a2a');
    BRACT.forEach((row, j) => { for (let i = 0; i < w; i++) { const ch = row[i]; if (BC[ch]) p.put(left ? bx + i + (5 - w) : bx + 4 - i, by + j, BC[ch]); } });
  }
  return p.done();
}
// the coconut palm at the left edge, leaning out over the cliff, its crown dark against the afterglow
const PALM = { base: [8, 222], ctrl: [-2, 140], top: [30, 74] };
const palmAt = s => { const a = 1 - s, [b, c, d] = [PALM.base, PALM.ctrl, PALM.top];
  return [a * a * b[0] + 2 * a * s * c[0] + s * s * d[0], a * a * b[1] + 2 * a * s * c[1] + s * s * d[1]]; };
function renderPalmTrunk() {
  const p = painter(60, 170, 0, 54);
  for (let k = 0, n = 420; k <= n; k++) {                                          // the trunk, ringed, lamplit low down
    const s = k / n, [x, y] = palmAt(s), w = Math.round(5 - 2.2 * s), ring = Math.round(y) % 4 === 0;
    const warm = Math.max(0, 1 - Math.hypot(x - LAMP.x, y - LAMP.y - 10) / 60);
    for (let j = 0; j < w; j++) p.put(x - (w >> 1) + j, y, ring ? '#140e10' : j === w - 1 ? mixHex('#3a2a28', '#c08050', warm) : j === 0 ? '#120c0e' : '#22181a');
  }
  return p.done();
}
function renderPalmCrown(a) {                         // the crown, swayed by a (-1..1)
  const p = painter(96, 70, 0, 44), [cx, cy] = PALM.top, r = rng(512), pts = [];
  [150, 175, 200, 228, 256, 288, 318, 346, 12, 34, 120].forEach(deg => {   // the fronds: rachis, drooping leaflets
    const th0 = (deg + (r() - 0.5) * 12) * Math.PI / 180, L = 20 + r() * 9, ch = Math.cos(th0), th = th0 + a * (0.06 + 0.04 * Math.abs(ch));
    const droop = 0.5 + 0.45 * Math.abs(ch) + a * 0.05 * Math.sign(ch);
    for (let s = 0; s <= 1.0001; s += 0.03) {
      const X = Math.round(cx + Math.cos(th) * L * s), Y = Math.round(cy + Math.sin(th) * L * s + droop * L * s * s);
      pts.push([X, Y, 3]);
      if (s > 0.12) {
        const len = Math.round(1 + 3.4 * Math.sin(Math.PI * Math.min(1, s * 1.05)));
        for (let k = 1; k <= len; k++) pts.push([X + (k > 2 ? (ch > 0 ? 1 : -1) : 0), Y + k, k === len ? 1 : 2]);
        if (s > 0.25 && Math.sin(th) < 0.2) pts.push([X, Y - 1, 4]);
      }
    }
  });
  const COL = ['', '#1a2a24', '#0c1612', '#14201a', '#5a4048'];                    // tips, leaflets, rachis, the glow's rim
  for (const [x, y, k] of pts) p.put(x, y, COL[k]);
  p.fill(cx - 2, cy - 1, 5, 4, '#0e1410');
  for (const [dx, dy] of [[-2, 2], [1, 2], [-1, 3], [2, 3]]) p.put(cx + dx, cy + dy, '#4a3420');   // coconuts
  return p.done();
}
// the path lamp on the left: a stone foot, a dark post, a lantern under a little peaked roof
function renderLamp() {
  const p = painter(16, 64, LAMP.x - 8, 146), X = LAMP.x;
  p.fill(X - 3, 199, 7, 6, '#3a3240'); p.fill(X - 3, 199, 7, 1, '#7a6a70'); p.fill(X - 2, 197, 5, 2, '#4a4050');
  for (let y = 167; y < 197; y++) { p.put(X, y, '#1c1418'); p.put(X + 1, y, y > 175 ? '#6a4a34' : '#3a2a24'); }
  p.fill(X - 3, 155, 7, 11, '#1e1418');
  for (let y = 156; y <= 164; y++) for (let i = X - 2; i <= X + 2; i++) p.put(i, y, y < 159 ? '#fff4d0' : y < 162 ? '#ffd890' : '#f0b060');
  p.fill(X - 2, 160, 5, 1, '#5a3a24'); p.put(X, 157, '#ffffff');
  for (let k = 0; k < 6; k++) p.fill(X - 4 + k, 154 - k, 9 - 2 * k, 1, k === 0 ? '#4a3026' : '#24181a');
  p.put(X, 147, '#8a6a3a');
  return p.done();
}

/* ================= The cozy corner: our warung, the frangipani, the penjor, the scooter ================= */
// A coconut stand under a tall roof of alang-alang grass on bamboo posts: a string of bulbs along the eave, two woven
// lamps inside, a back wall of woven bamboo with shelves of bottles and strips of snack sachets, the counter heaped
// with young coconuts, a jar of krupuk and the cat asleep at its end; a stone plinth with steps where the morning's
// canang sari still sit with a stick of incense.
const ALANG = ['#1a120e', '#261a12', '#342416', '#44301a', '#56401e', '#6a5024', '#80622a', '#987432', '#b2883c', '#c89c48',
  '#dcb058', '#ecc46a'];
const roofL = y => Math.round(WARUNG.x0 + (WARUNG.eave - y) * 1.0);           // the roof's hip, eave to ridge
const BEDEG = ['#2e1c12', '#46301c', '#5e4026', '#76522e', '#8e6438', '#a67842', '#bc8c4e', '#d0a05e', '#e2b672'];
const W_LAMPS = [[398, 152], [446, 152]];
const EAVE_BULBS = [];
for (let x = 360; x < W; x += 7) EAVE_BULBS.push([x, WARUNG.eave + 3 + ((x / 7) & 1)]);
const COCO = ['.gGGg.', 'gGHHGg', 'gGHGGg', 'gGGGGd', '.gGGd.', '..dd..'];
const COCO_OPEN = ['....r.', '...w..', '..rw..', '.cWWc.', 'cGGGGc', 'gGHGGd', '.gGGd.'];
const COCO_C = { g: '#3a6424', G: '#5a8a30', H: '#a0c050', d: '#24401a', c: '#c8d898', W: '#f4f0dc', w: '#f0f0f0', r: '#d83a3a' };
const CAT = ['.oo.......', 'oKoo.ooo..', 'ooooooooo.', 'oWooooooOo', '.ooooooOO.'];
const CAT_C = { o: '#d8823a', O: '#a85a24', K: '#2a1a10', W: '#f0e0c8' };
const CANANG = ['.yrwp.', 'gGGGGg', '.gggg.'];
const CANANG_C = { y: '#f0c040', r: '#e84a5a', w: '#f8f4ea', p: '#b04ac0', G: '#d0dc8e', g: '#8aa05a' };
const OFFERINGS = [[357, 201], [366, 196], [386, 192]];                     // canang sari on the steps and the floor's edge
function renderWarung() {
  const { x0, eave, ridge, counter, floor } = WARUNG, p = painter(W - x0 + 8, H - 90, x0 - 8, 90), P = p.put, F = p.fill;
  // inside: the back wall of woven bamboo, lit by the lamps
  for (let y = eave + 3; y < counter; y++) for (let x = x0 + 5; x < W; x++) {
    let l = 0;
    for (const [lx, ly] of W_LAMPS) l = Math.max(l, 1 - Math.hypot((x - lx) / 46, (y - ly) / 30));
    const weave = (((x >> 1) + (y >> 1)) & 1) ? 0.07 : -0.07;
    P(x, y, pick(BEDEG, 0.18 + 0.52 * l + weave, x, y));
  }
  F(x0 + 5, eave + 3, W - x0 - 5, 2, '#2a1a12');
  for (const sy of [157, 168]) {                                               // shelves, bottles and jars
    F(384, sy, 86, 1, '#e0a868'); F(384, sy + 1, 86, 1, '#5a3a22');
    for (let x = 386; x < 468; x += 2) {
      if (hash(x, sy, 101) < 0.25) continue;
      const hgt = 3 + Math.floor(hash(x, sy, 102) * 3), col = ['#3a7a4a', '#c84a3a', '#e8c040', '#e8e8e0', '#4a6ab0', '#8a4a2a'][Math.floor(hash(x, sy, 103) * 6)];
      F(x, sy - hgt, 1, hgt, col); P(x, sy - hgt, mixHex(col, '#ffffff', 0.4));
    }
  }
  // the roof: alang-alang in thick courses, its lower edge warm from the bulbs, a black ijuk ridge
  for (let y = ridge; y <= eave; y++) for (let x = roofL(y); x < W; x++) {
    const c = Math.floor((y - ridge) / 6), f = frac((y - ridge) / 6);
    let v = 0.16 + 0.4 * smooth(ridge + 6, eave + 2, y) + (hash(x, c, 104) - 0.5) * 0.14 + ((x + c * 2) % 3 === 0 ? -0.05 : 0);
    if (f > 0.8) v -= 0.14;                                                    // the shadow under each course
    if (x - roofL(y) < 2) v += 0.08;                                           // the hip's edge
    P(x, y, pick(ALANG, v, x, y));
  }
  for (let x = roofL(eave) - 3; x < W; x++) {                                  // the thick, ragged eave
    const n = 2 + Math.floor(hash(x, 7, 105) * 3);
    for (let k = 1; k <= n; k++) P(x, eave + k, k === n ? '#a07434' : k === 1 ? '#f0c470' : '#d8a450');
  }
  for (let x = roofL(ridge) - 2; x < W; x++) { P(x, ridge - 1, '#120c0e'); P(x, ridge - 2, '#1e1618'); P(x, ridge - 3, x % 4 ? '#2a2024' : '#120c0e'); }
  for (let k = 0; k < 5; k++) P(roofL(ridge) - 2 - Math.round(k * 0.5), ridge - 2 - k, '#1e1618');   // the ridge's upturned end
  // posts
  for (const [px0, w] of [[x0 + 3, 3], [W - 3, 3]]) for (let y = eave + 4; y < floor; y++) for (let k = 0; k < w; k++)
    P(px0 + k, y, (y - eave) % 11 === 0 ? '#5a4428' : k === 0 ? '#d8b878' : k === w - 1 ? '#7a5a32' : '#b09060');
  const back = p.done(), q = painter(W - x0 + 8, H - 90, x0 - 8, 90);
  return { back, front: renderWarungFront(q) };
}
function renderWarungFront(p) {
  const { x0, counter, floor } = WARUNG, P = p.put, F = p.fill;
  // the counter: a lit top, a front of bamboo slats
  F(384, counter, 92, 1, '#ecb876'); F(384, counter + 1, 92, 1, '#8a5a34');
  for (let y = counter + 2; y < floor; y++) for (let x = 384; x < 476; x++) {
    const sl = (x - 384) % 3;
    let col = sl === 2 ? '#4a3020' : sl === 0 ? '#c0925a' : '#9c7040';
    if ((y - counter) % 8 === 0) col = '#6a4626';
    P(x, y, col);
  }
  // on the counter: the heap of young coconuts, one opened with a straw, the krupuk jar, the cat
  for (const [cx, cy] of [[386, 171], [392, 171], [398, 171], [404, 171], [389, 166], [395, 166], [401, 166], [392, 161], [398, 161]])
    COCO.forEach((row, j) => { for (let i = 0; i < 6; i++) if (COCO_C[row[i]]) P(cx + i, cy + j - 1, COCO_C[row[i]]); });
  COCO_OPEN.forEach((row, j) => { for (let i = 0; i < 6; i++) if (COCO_C[row[i]]) P(412 + i, 169 + j, COCO_C[row[i]]); });
  F(424, 165, 8, 11, '#9ab8c8'); F(425, 166, 6, 9, '#e8dca0'); F(425, 167, 2, 7, '#f8f0c8'); F(424, 164, 8, 1, '#c8d8e0');
  CAT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (CAT_C[row[i]]) P(458 + i, 171 + j, CAT_C[row[i]]); });
  // the plinth: dressed stone under a lit edge; three steps up at its left end
  F(x0 + 2, floor, W - x0 - 2, 1, '#b8a89c');
  for (let y = floor + 1; y < 207; y++) for (let x = x0 + 18; x < W; x++) {
    const c = Math.floor((y - floor - 1) / 5), joint = (y - floor - 1) % 5 === 4 || (x + c * 5) % 11 === 0;
    P(x, y, lit(x, y, joint ? '#261e24' : y === floor + 1 ? '#6a5a5e' : '#4a3e46'));
  }
  F(x0 + 2, floor + 1, 16, 3, '#3e3238');
  F(x0, 196, 18, 1, '#a89a90'); F(x0, 197, 18, 4, '#3a2e34'); P(x0, 197, '#5a4a50');
  F(x0 - 3, 201, 21, 1, '#9a8c86'); F(x0 - 3, 202, 21, 4, '#30262c'); P(x0 - 3, 202, '#504046');
  for (const [ox, oy] of OFFERINGS) CANANG.forEach((row, j) => { for (let i = 0; i < 6; i++) if (CANANG_C[row[i]]) P(ox + i, oy + j - 3, CANANG_C[row[i]]); });
  return p.done();
}
// the frangipani: a smooth grey trunk forking into thick branches, rosettes of long leaves at their tips, flowers
const FR_BRANCH = [[[340, 202], [339, 186], [337, 172]], [[337, 172], [328, 158], [318, 146]], [[337, 172], [342, 156], [345, 138]],
  [[338, 174], [349, 162], [356, 150]], [[328, 158], [318, 158], [308, 156]], [[342, 156], [334, 144], [330, 132]], [[318, 146], [312, 138]],
  [[345, 138], [352, 130]]];
const FR_ROSETTES = [[318, 144], [345, 136], [356, 148], [308, 155], [330, 130], [312, 136], [352, 128], [326, 150]];
function renderFrangipani() {
  const p = painter(80, 96, 296, 112), P = p.put;
  FR_BRANCH.forEach((pts, bi) => {
    for (let k = 0; k + 1 < pts.length; k++) {
      const [ax, ay] = pts[k], [bx, by] = pts[k + 1], n = Math.max(Math.abs(bx - ax), Math.abs(by - ay)) * 2;
      for (let j = 0; j <= n; j++) {
        const x = Math.round(lerp(ax, bx, j / n)), y = Math.round(lerp(ay, by, j / n)), w = bi === 0 ? 4 : bi < 4 ? 3 : 2;
        for (let q = 0; q < w; q++) P(x - (w >> 1) + q, y, q === w - 1 ? '#b09484' : q === 0 ? '#4a3c3c' : '#7a6a62');
      }
    }
  });
  const r = rng(131);
  for (const [cx, cy] of FR_ROSETTES) {
    for (let k = 0; k < 11; k++) {                                             // long leaves radiating from the tip
      const a = -Math.PI / 2 + (k - 5) * 0.36 + (r() - 0.5) * 0.2, L = 7 + r() * 5;
      for (let s = 0; s <= L; s += 0.5) {
        const x = Math.round(cx + Math.cos(a) * s), y = Math.round(cy + Math.sin(a) * s * 0.8 + s * s * 0.03);
        const wd = s > 1 && s < L - 1.5 ? 1 : 0;
        const col = x > cx + 2 ? '#3e5e30' : s > L * 0.6 ? '#2a4a28' : '#1a3220';
        P(x, y, col); if (wd) P(x + (Math.cos(a) > 0 ? 0 : 1), y + 1, '#14281a');
        if (hash(x, y, 85) < 0.3) LEAF_PX.push([x, y, x > cx + 2 ? '#5a7e3e' : '#3a5a32']);
      }
    }
    for (let k = 0; k < 7; k++) {                                              // a cluster of flowers on top
      const fx = Math.round(cx + (r() - 0.5) * 9), fy = Math.round(cy - 3 - r() * 5);
      P(fx, fy, '#f2c848'); P(fx - 1, fy, '#f8f2e4'); P(fx + 1, fy, '#f8f2e4'); P(fx, fy - 1, '#fffaf0'); P(fx, fy + 1, '#d8d0c4');
    }
  }
  return p.done();
}
// the penjor: a tall bamboo pole arching over the corner, decked with young coconut leaf; the sampian swings at its tip
const SAMPIAN = ['..YYY..', '.YyYyY.', 'YyYYYyY', '.YyYyY.', '..YYY..', '...y...', '..YyY..', '...y...', '..y.y..', '..y.y..'];
const penjorAt = (s, sw = 0) => { const a = 1 - s, [b, c, d0] = [PENJOR.base, PENJOR.ctrl, PENJOR.tip], d = [d0[0] + sw * 2, d0[1] - Math.abs(sw)];
  return [a * a * b[0] + 2 * a * s * c[0] + s * s * d[0], a * a * b[1] + 2 * a * s * c[1] + s * s * d[1]]; };
function renderPenjor(sw = 0) {
  const p = painter(100, H - 18, 384, 18), P = p.put;
  for (let k = 0, n = 700; k <= n; k++) {
    const s = k / n, [x, y] = penjorAt(s, sw), w = s < 0.35 ? 3 : s < 0.75 ? 2 : 1;
    for (let q = 0; q < w; q++) P(Math.round(x) - (w >> 1) + q, Math.round(y), q === 0 ? '#e0cc88' : q === w - 1 ? '#7a6634' : '#b09a58');
    if (s > 0.5 && k % 6 === 0) {                                              // janur fringes hanging off the arch
      const len = 2 + Math.floor(hash(k, 1, 111) * 4);
      for (let j = 1; j <= len; j++) P(Math.round(x) + (j > 2 ? 1 : 0), Math.round(y) + 1 + j, j === len ? '#a8b060' : '#e4e2a0');
    }
    if (s > 0.18 && s < 0.45 && k % 22 === 0) { P(Math.round(x) - 2, Math.round(y), '#e4e2a0'); P(Math.round(x) - 3, Math.round(y) + 1, '#c8c878'); }
  }
  const [sx, sy] = penjorAt(0.22);                                             // the little sanggah halfway up, its offering
  p.fill(Math.round(sx) - 5, Math.round(sy), 7, 6, '#c8a868'); p.fill(Math.round(sx) - 4, Math.round(sy) + 1, 5, 3, '#4a3020');
  P(Math.round(sx) - 3, Math.round(sy) + 2, '#f0c040'); P(Math.round(sx) - 2, Math.round(sy) + 2, '#e84a5a');
  p.fill(Math.round(sx) - 6, Math.round(sy) - 1, 9, 1, '#2a2024'); p.fill(Math.round(sx) - 5, Math.round(sy) - 2, 7, 1, '#2a2024');
  return p.done();
}
// a scooter parked on the path, a helmet on its seat
const SCOOTER = ['..m.........................', '..kk........................', '...k..............RRR.......',
  '..kkk............RRRRr......', '...BB..........sssssss......', '...BBB........sssssssss.....', '..BBBB.....bBBBBBBBBBBBB....',
  '..BBBB....bBBBBBBBBBBBBBB...', '.LBBBB...bBBBBBBBBBBBBBBBt..', '..BBBBffffffffBBBBBBBBBB....', '...BBb.......bkkkkkkkb......',
  '..wkkw.............wkkw.....', '.wkookw...........wkookw....', '.wkookw...........wkookw....', '..wkkw.............wkkw.....'];
const SCOOTER_C = { m: '#8a8a94', k: '#16121a', B: '#7ac0b4', b: '#3e7a74', s: '#241c22', f: '#3a3640', L: '#f4ecd0', t: '#c83a3a',
  R: '#d84a3a', r: '#7a2a24', w: '#3a3640', o: '#7a7884' };
const SCOOT = { x: 298, y: 192 };

/* ================= The warung's painted sign on its ridge (the billboard) ================= */
// A board under its own little ijuk roof on two bamboo uprights from the ridge, lit by two bulbs under that roof. The
// warung's name, WARUNG SENJA ("the dusk warung"), in the scene's own wide letters (N, M and W wider than the 3 px font
// allows), and a young coconut with a straw. Painted bright, then night-graded so it stays under the floodlit rock.
const WIDE = {
  W: ['10001', '10001', '10101', '10101', '01010'], A: ['0110', '1001', '1111', '1001', '1001'], R: ['1110', '1001', '1110', '1010', '1001'],
  U: ['1001', '1001', '1001', '1001', '0110'], N: ['1001', '1101', '1011', '1001', '1001'], G: ['0111', '1000', '1011', '1001', '0111'],
  S: ['0111', '1000', '0110', '0001', '1110'], E: ['111', '100', '110', '100', '111'], J: ['0011', '0001', '0001', '1001', '0110'],
};
const wideW = s => [...s].reduce((a, ch) => a + WIDE[ch][0].length + 1, -1);
function wideText(p, s, x, y, col) {
  for (const ch of s) { WIDE[ch].forEach((row, j) => [...row].forEach((b, i) => { if (b === '1') p.put(x + i, y + j, col); })); x += WIDE[ch][0].length + 1; }
}
const BB_LAMPS = [BB.x + 9, BB.x + BB.w - 10];
const BB_GRADE = { level: 0.8, tint: '#1a1e44', mix: 0.12, wear: 0.32, seed: 17 };
function renderAd() {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < BB.h; y++) for (let x = 0; x < BB.w; x++) {
    let l = 0;
    for (const lx of BB_LAMPS) l = Math.max(l, 1 - Math.hypot((x - lx + BB.x) / 16, y / 14));
    p.put(x, y, pick(['#0a2a34', '#0e3640', '#13444e', '#18525c', '#1e626a', '#267478', '#30888a'], 0.25 + 0.6 * l - y / BB.h * 0.2, x, y));
  }
  const ICON = ['.......rr..', '......r....', '.....r.....', '..cWWWc....', '.cWWWWWc...', 'gGGGGGGGg..', 'gGHGGGGGd..', 'gGGGGGGGd..',
    '.gGGGGGd...', '..gGGGd....', '...ddd.....'];
  ICON.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (COCO_C[row[i]]) p.put(4 + i, 4 + j, COCO_C[row[i]]); });
  const tx = 18;
  wideText(p, 'WARUNG', tx + 1, 4, '#06202a'); wideText(p, 'WARUNG', tx, 3, '#f6ead0');
  const sx = tx + Math.round((wideW('WARUNG') - wideW('SENJA')) / 2);
  wideText(p, 'SENJA', sx + 1, 11, '#06202a'); wideText(p, 'SENJA', sx, 10, '#f8b04a');
  for (let x = tx; x < tx + wideW('WARUNG'); x++) p.put(x, 17 + ((x >> 1) & 1), '#5ad0d8');   // a little wave under it
  return gradeAd(p.done().c, BB_GRADE);
}
function renderBillboard() {                          // uprights, frame, the little roof (only with the billboard)
  const p = painter(BB.w + 14, BB.h + 48, BB.x - 7, BB.y - 8), P = p.put, F = p.fill;
  for (const ux of [BB.x + 5, BB.x + BB.w - 7]) { F(ux, BB.y + BB.h + 1, 2, WARUNG.ridge - BB.y - BB.h, '#b09060'); P(ux + 1, BB.y + BB.h + 1, '#6a5030'); }
  F(BB.x - 1, BB.y - 1, BB.w + 2, BB.h + 2, '#2a1810'); F(BB.x - 1, BB.y + BB.h, BB.w + 2, 1, '#4a2e1c');
  for (let k = 0; k < 5; k++) F(BB.x - 4 + k * 2, BB.y - 2 - k, BB.w + 8 - k * 4, 1, k === 0 ? '#4a3020' : '#1a1214');
  return p.done();
}

/* ================= People ================= */
// One function draws everyone near us (after taipei.js and rome.js): a look gives the proportions and the colours,
// dimmed for the blue hour; shoulders, flank and hair catch a line of the afterglow or of the warung's warm light.
const R = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(x, y, w, h); };
const SKINS = ['#e8c098', '#d0a074', '#b07e56', '#8a5e3e'], HAIRS = ['#1e1614', '#3a2620', '#5a3a26', '#7a5a3a'];
const NIGHT = '#0c0e24';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk), lc = o.lit || '#f0a080';
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], lc, 0.42), top: m(top), topD: mixHex(top, NIGHT, dk + 0.25),
    topL: mixHex(top, lc, 0.3), bot: m(bot), botL: m(o.botL || bot), shoe: m(o.shoe || '#1a161c'), long: o.long || 0,
    cap: o.cap ? m(o.cap) : null, udeng: o.udeng ? m(o.udeng) : null, bun: !!o.bun, sash: o.sash ? m(o.sash) : null, sarong: !!o.sarong };
}
/** one person, feet on row fy, centred on column x. o.view: 0 from behind, 1 facing us, 2 in profile (o.dir);
 *  o.aL / o.aR: 0 down, 1 up, 2 to the face, 3 out, 4 up and out, 5 round a neighbour; o.sit; o.bend; o.lean; o.step */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bend = side && o.bend ? 2 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir;
  if (legs) {
    const ly = fy - legs + 1;
    if (!o.noShadow) { ctx.fillStyle = 'rgba(6,6,16,0.4)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); }
    if (P.sarong) {
      const sw = side ? 3 : bw - 1, sx = side ? x - 1 : tx;
      R(sx, ly, sw, legs - 1, P.bot); R(sx, ly + 2, sw, 1, P.botL); R(sx, ly + legs - 4, sw, 1, P.botL); R(sx, fy, sw, 1, P.shoe);
    } else if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2, sp = o.step ? 1 : 0;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2 - sp, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      R(tx, fy - sp, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty + 1, bw, th - 1, P.top); R(tx + 1, ty, bw - 2, 1, P.top);                     // rounded shoulders
  R(side ? (dir > 0 ? tx : tx + bw - 1) : tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) { R(tx + 1, ty, bw - 2, 1, P.topL); R(tx, ty + 1, 1, th - 2, P.topL); }
  if (P.sash) R(tx, ty + th - 2, bw, 1, P.sash);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else if (st === 5) R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD);
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir;
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 + bend : x - 3 - bend, ty + 3, 3, 1, P.topD); R(x + dir * (4 + bend), ty + 3, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, P.topD); R(x + dir * 4, ty - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hY = hy + bend;
  if (view === 0) {
    R(hx, hY + 1, hw, hh - 1, P.hair); R(hx + 1, hY, hw - 2, 1, P.hairL); R(hx, hY + 1, 1, hh - 2, P.hairL);
    if (P.long) { R(hx, hY + hh, hw, P.long, P.hair); R(hx, hY + hh, 1, P.long - 1, P.hairL); } else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, 1, P.hair); }
  else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, 1, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.bun) R(side ? (dir > 0 ? hx - 1 : hx + hw - 1) : hx + (hw >> 1) - 1, hY - 1, 2, 2, P.hair);
  if (P.udeng) { R(hx - 1, hY, hw + 2, 2, P.udeng); R(hx + hw - 1, hY - 1, 2, 1, P.udeng); }
  if (P.cap) { R(hx, hY - 1, hw, 2, P.cap); if (side) R(dir > 0 ? hx + hw : hx - 2, hY, 2, 1, P.cap); }
  return hY;
}
/** a small figure out on the reef, h px tall (11 to 14): head, shoulders, top, a sarong or trousers, stepping legs, and a
 *  dim reflection under it on the wet sand. o: { top, bot, hair, white (a white udeng), step } */
function tiny(x, fy, h, top, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const bot = o.bot || '#2a2438', hair = o.hair || '#1e1614', skin = '#a87858', a0 = ctx.globalAlpha;
  const hd = 3, th = Math.round(h * 0.34), lg = h - hd - th, step = o.step ? 1 : 0;
  ctx.globalAlpha = a0 * 0.2; R(x - 1, fy + 1, 4, Math.round(h * 0.6), top); ctx.globalAlpha = a0;
  R(x - 1, fy - lg + 1, 4, lg - 2, bot);                                      // sarong or trousers
  R(x - 1 + step, fy - 1, 1, 2, '#141018'); R(x + 2 - step, fy - 1, 1, 2, '#141018');   // legs, a stride
  R(x - 1, fy - lg - th + 1, 4, th, top); R(x - 1, fy - lg - th + 1, 1, th, mixHex(top, '#ffd0a0', 0.25));   // lit by the floods
  R(x, fy - h + 1, 2, hd, skin); R(x, fy - h + 1, 2, 1, o.white ? '#f0ecf4' : hair);
  if (!o.white) px(ctx, x + 1, fy - h + 2, hair);
}
const PEOPLE = {
  she: look(24, 0, 1, '#d86a5a', '#2a3048', { long: 5 }), he: look(26, 1, 0, '#e8e4dc', '#3a4a5a'),
  photo: look(25, 2, 0, '#2e5a6a', '#2a2a34', { cap: '#c8423a' }), kid: look(16, 2, 0, '#e8b040', '#2a3a5a', { dk: 0.25 }),
  ibu: look(24, 2, 0, '#ece4d8', '#7a3a2a', { sarong: true, botL: '#c89a4a', sash: '#d8a830', bun: true, dk: 0.18, lit: '#ffc890' }),
  keeper: look(24, 3, 0, '#2a6a5a', '#2a2a34', { udeng: '#e8e4dc', dk: 0.12, lit: '#ffc890' }),
  guest: look(24, 0, 2, '#4a7ab0', '#2a2a34', { long: 4, dk: 0.15, lit: '#ffb880' }),
};
const DOG = ['.e.........', 'eTe...ttt..', 'TTTTTTTTTt.', 'tTTTTTTTTtt', '.tKtttttKt.'];   // asleep on its side, tail round
const DOG_C = { t: '#7a5a3e', T: '#b08a5a', e: '#5a4030', K: '#4a3426' };

/* ================= The loop's timeline (seconds; the table is in the header comment) ================= */
const SETS = [{ a: 56, burst: 71, b: 88 }, { a: 184, burst: 199, b: 216 }];   // big sets: three swells, the third bursts
const PROC = { a: 94, walk: 56, rest: 6, climb: 16, gap: 3.5 };               // the procession: start, then each leg's length
const LAMP_ON = 158, LAMP_OFF = 222;                                           // the temple's lamps, one by one
const FOXES = [[4, 20], [206, 222]];                                           // flying foxes cross the bay
const DIVES = [[36, 46], [226, 234]];                                          // the janggan dives and climbs again
const WAITER = [20, 80, 140, 200];                                             // the waiter's trips with coconuts (22 s each)
const STROLL = [{ a: 30, b: 66, dir: -1 }, { a: 150, b: 186, dir: 1 }];       // a couple strolls along the path
const DOG_UP = [118, 128];                                                     // the dog gets up, turns round, lies down
const WALL_CAT = [{ a: 100, b: 150, dir: 1 }, { a: 228 - 50, b: 228, dir: -1 }];   // a cat walks the wall's cap
const BREAKERS = 15;                                                           // a breaker along the reef every 16 s

/* ================= The crowd ================= */
// The people the scene shows by default; render(t, { crowd }) thins them by rank, 0 draws nobody at all. Rank 0, there
// at any crowd above 0: the keeper, the woman with the offerings, the waiter and the procession.
const CROWD = 0.5;
const RANK = { couple: 0.1, reefA: 0.15, guest: 0.2, photo: 0.3, fisher: 0.35, bags: 0.42, stroll: 0.48, reefB: 0.58,
  kid: 0.64, reader: 0.76, reefC: 0.86 };

/* ================= Sky life ================= */
function drawClouds(t) {                               // two strips drifting, one and two frame widths a loop
  for (const [C, k] of [[CLOUD2_C, 2], [CLOUD_C, 1]]) {
    const off = Math.floor(frac(k * t / LOOP) * W);
    ctx.drawImage(C.c, off - W, 0); ctx.drawImage(C.c, off, 0);
  }
}
function drawFoxes(t) {                               // a loose group of flying foxes leaving the cliff over the bay
  for (const [a, b] of FOXES) {
    if (t < a || t >= b) continue;
    const u = (t - a) / (b - a);
    for (let k = 0; k < 6; k++) {
      const x = Math.round(lerp(490, -96, u) + k * 15 + 5 * Math.sin(k * 2.1)), y = Math.round(40 + k * 3 + 4 * Math.sin(k * 1.3) - 8 * Math.sin(Math.PI * u));
      const up = (Math.floor(t * 6) + k) & 1;
      R(x - 1, y, 3, 1, '#120e1a'); R(x - 3, y + (up ? -1 : 1), 2, 1, '#18121e'); R(x + 2, y + (up ? -1 : 1), 2, 1, '#18121e');
    }
  }
}
const diveAt = t => { for (const [a, b] of DIVES) if (t >= a && t < b) return Math.sin(Math.PI * (t - a) / (b - a)); return 0; };

/* ================= Sea life ================= */
function drawJukung(t) {                              // under sail, home before dark: across the glow and behind the island
  const u = frac(t / 120), x = Math.round(lerp(-18, 96, u)), y = JUKUNG.y + Math.round(0.6 * wave(t, 40));
  ctx.drawImage(JUK_C, x, y);
  ctx.globalAlpha = 0.25; ctx.drawImage(JUK_C, 0, 8, 15, 3, x, y + 13, 15, 3); ctx.globalAlpha = 1;
  px(ctx, x + 12, y + 7, '#ffd890'); halo(HL.lamp, x + 12, y + 7, 0.7);
}
const setAmp = t => SETS.reduce((m, S) => Math.max(m, smooth(S.a, S.burst, t) * (1 - smooth(S.burst + 2, S.b, t))), 0);
function drawSwells(t) {                              // long low swells rolling in towards the reef, bigger in a set
  const amp = setAmp(t);
  for (let j = 0; j < 3; j++) {
    const { cyc, u } = cycle(t, BREAKERS, j / 3), y = Math.round(lerp(HZ + 3, 147, u ** 1.3));
    ctx.globalAlpha = Math.sin(Math.PI * u) * (0.18 + 0.4 * amp);
    for (let x = 0; x < W; x++) if (vnoise(x / 13, cyc * 3 + j, 152) > 0.42 - 0.2 * amp) {
      px(ctx, x, y, '#8aa4cc'); if (amp > 0.4) px(ctx, x, y + 1, '#1a2448');
    }
  }
  ctx.globalAlpha = 1;
}
// the breakers along the reef edge: a swell lifting, a white line where it tumbles (peeling from the far ends of the
// reef towards the island), then lace spreading over the flat and fading; a set makes them taller and whiter
function drawSurf(t) {
  const { cyc, u } = cycle(t, BREAKERS), s = step(t, 480), amp = setAmp(t);
  for (let x = 0; x < W; x++) {
    if (x > TL.x0 - 3 && x < TL.x1 + 3) continue;
    const y0 = Math.round(reefEdge(x)), peel = x < TL.x0 ? 1 - x / TL.x0 : (W - x) / (W - TL.x1);
    const ux = u - 0.18 * peel, gap = hash(x >> 2, cyc, 153) < 0.2;
    if (ux > 0.3 && ux < 0.55 && !gap) { ctx.globalAlpha = 0.5; px(ctx, x, y0 - Math.round((0.55 - ux) * 12), '#9ab4d4'); }
    else if (ux >= 0.55 && ux < 0.68 && !gap) {
      ctx.globalAlpha = 1; px(ctx, x, y0, '#f0f8f8'); px(ctx, x, y0 + 1, '#b8d0dc');
      if (hash(x, s, 154) < 0.5 + amp * 0.4) px(ctx, x, y0 - 1 - Math.floor(hash(x, s, 155) * (2 + amp * 6)), '#ffffff');
    } else if (ux >= 0.68 && ux < 1) {
      const f = 1 - (ux - 0.68) / 0.32, yy = y0 + 2 + Math.round((ux - 0.68) * (14 + amp * 10));
      if (hash(x, cyc, 156) < 0.55) { ctx.globalAlpha = 0.6 * f; px(ctx, x, yy, '#d8e8ec'); }
      if (hash(x, cyc, 157) < 0.25) { ctx.globalAlpha = 0.35 * f; px(ctx, x, yy - 2, '#a8c0cc'); }
    }
  }
  ctx.globalAlpha = 1;
}
// the burst: spray climbing the island's seaward end, then foam flooding the reef flat and drawing back
function drawSpray(t) {
  for (const S of SETS) {
    const tau = t - S.burst;
    if (tau < -0.4 || tau > 8) continue;
    halo(HL.mist, 100, Math.round(150 - 34 * Math.min(1, Math.max(0, tau) / 2)), 0.8 * Math.min(1, (tau + 0.4) * 2) * (1 - Math.max(0, tau) / 8));
    for (let k = 0; k < 34; k++) {                     // the plume: soft white water thrown up the seaward end
      const tt = tau - hash(k, 6, 151) * 0.6;
      if (tt < 0) continue;
      const v0 = 26 + hash(k, 7, 151) * 40, x = 80 + hash(k, 8, 151) * 34 + (hash(k, 9, 151) - 0.35) * 12 * tt, y = 162 - v0 * tt + 11 * tt * tt;
      if (y > 166) continue;
      const r = Math.min(6, 2 + Math.floor(tt * 2.2 + hash(k, 10, 151) * 2));
      ctx.globalAlpha = Math.max(0, 1 - tt / 5.2) * 0.9;
      ctx.drawImage(SPRAY[r], Math.round(x) - r, Math.round(y) - r);
    }
    for (let k = 0; k < 70; k++) {                     // droplets flung round its edges
      const tt = tau - hash(k, 4, 151) * 0.8;
      if (tt < 0) continue;
      const v0 = 24 + hash(k, 2, 151) * 44, x = 74 + hash(k, 1, 151) * 48 + (hash(k, 3, 151) - 0.5) * 22 * tt, y = 160 - v0 * tt + 11 * tt * tt;
      if (y > 163) continue;
      ctx.globalAlpha = Math.max(0, 1 - tt / 6) * 0.8;
      px(ctx, Math.round(x), Math.round(y), tt < 1.4 ? '#ffffff' : '#c8dce4');
    }
    ctx.globalAlpha = 1;
  }
}
function drawFlood(t) {
  for (const S of SETS) {
    const tau = t - S.burst;
    if (tau < 0.6 || tau > 16) continue;
    const reach = smooth(0.6, 5, tau), back = smooth(6, 16, tau), front = lerp(162, 184, reach) - (184 - 164) * back * 0.6;
    for (let x = 0; x < 344; x++) {
      const top = x < TL.x0 ? Math.round(reefEdge(x)) + 2 : TL.base + 1;
      if (front <= top) continue;
      const fy = Math.round(front + 1.5 * Math.sin(x * 0.21 + tau));
      ctx.globalAlpha = 0.85 * (1 - back); if (hash(x, 1, 158) < 0.8) px(ctx, x, fy, '#e8f4f4');
      for (let y = top; y < fy; y++) if (hash(x, y, 159) < 0.16) { ctx.globalAlpha = 0.45 * (1 - back) * (1 - (fy - y) / 30); px(ctx, x, y, '#c8dce4'); }
    }
    ctx.globalAlpha = 1;
  }
}
const RIPPLES = [];
{ const r = rng(161); for (let i = 0; i < 18; i++) RIPPLES.push({ id: i, x: 20 + Math.floor(r() * 440), y: 165 + Math.floor(r() * 19), w: 3 + Math.floor(r() * 5), n: 30 + Math.floor(r() * 30), ph: r() }); }
function drawRipples(t) {                             // the thin water on the reef stirs and glints
  for (const r of RIPPLES) {
    const { cyc, u } = cycle(t, r.n, r.ph), x = r.x + Math.round((hash(r.id, cyc, 162) - 0.5) * 30);
    if (inRock(x, r.y) || hash(r.id, cyc, 163) < 0.3) continue;
    ctx.globalAlpha = Math.sin(Math.PI * u) * 0.35;
    R(x, r.y, r.w, 1, Math.abs(x - TL.cx) < 120 ? '#d8a060' : '#9a98c8');
  }
  ctx.globalAlpha = 1;
}

/* ================= The temple's lamps, the procession, the people on the reef ================= */
// the lamps on the terrace wall come on one by one from the gate as the procession climbs, and go out again before
// the loop ends; the lamp at the gate and the spring's glow burn all loop
const TLAMPS = [238, 222, 198, 172, 150, 128, 110].map((x, i) => ({ x, on: LAMP_ON + i * 2, off: LAMP_OFF + i * 2 }));
const lampLevel = (L, t) => smooth(L.on, L.on + 1.2, t) * (1 - smooth(L.off, L.off + 1.2, t));
function drawTempleLamps(t) {
  for (const L of TLAMPS) {
    const a = lampLevel(L, t), y = dome(L.x) - 7;
    if (a <= 0.01) { px(ctx, L.x, y, '#3a2a24'); continue; }
    ctx.globalAlpha = a; R(L.x, y - 1, 1, 2, '#fff0c0'); ctx.globalAlpha = 1;
    halo(HL.tlamp, L.x, y, a); halo(HL.tlampCore, L.x, y, a);
  }
  px(ctx, 319, 108, '#ffe0a0'); halo(HL.tlamp, 319, 108, 0.9 + 0.1 * wave(t, 30));
}
// in from the stair at the foot of our cliff (left), along the bared sand in front of the island to the spring, then up
// the steps and through the gate
const PROC_PATH = [[-12, 182], [60, 181], [140, 180], [210, 180], [258, 177], [284, 171], [300, 166]];
const PROC_CAST = ['umb', 'fruit', 'fruit', 'canang', 'bell', 'priest'];
function along(pts, s) {
  const seg = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) { const d = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]); seg.push(d); total += d; }
  let d = clamp(s, 0, 1) * total;
  for (let i = 0; i < seg.length; i++) {
    if (d <= seg[i]) { const u = d / seg[i]; return [lerp(pts[i][0], pts[i + 1][0], u), lerp(pts[i][1], pts[i + 1][1], u)]; }
    d -= seg[i];
  }
  return pts[pts.length - 1];
}
function procPos(k, t) {                              // [x, feet, height, alpha, walking] of member k, or null
  const tm = t - PROC.a - k * PROC.gap, { walk, rest, climb } = PROC;
  if (tm < 0 || tm > walk + rest + climb + 4) return null;
  if (tm < walk) { const [x, y] = along(PROC_PATH, tm / walk); return [x, y, lerp(14, 11, (182 - y) / 16), 1, true]; }
  if (tm < walk + rest) return [300 - k * 2, 166, 11, 1, false];               // blessed at the spring
  const u = clamp((tm - walk - rest) / climb, 0, 1);
  return [lerp(STEPS.x0, STEPS.x1, u), lerp(STEPS.y0, STEPS.y1, u), 9, 1 - smooth(climb, climb + 4, tm - walk - rest), true];
}
function procFigure(x, fy, h, kind, walking, t) {
  x = Math.round(x); fy = Math.round(fy); h = Math.round(h);
  const step = walking && (Math.floor(t * 3.2) & 1), top = h - 3;
  const look = { umb: ['#2a2a3a', '#e8e4dc'], fruit: ['#f0ece4', '#a83a3a'], canang: ['#f0ece4', '#3a5aa8'], bell: ['#ece8f0', '#4a4a5a'], priest: ['#f4f0f8', '#e8e4ec'] }[kind];
  tiny(x, fy, h, look[0], { bot: look[1], white: kind === 'umb' || kind === 'bell' || kind === 'priest', step });
  if (kind === 'fruit') R(x - 1, fy - lg0(h) - Math.round(h * 0.34) + 2, 4, 1, '#d8a830');                 // her sash
  const hy = fy - h + 1;
  if (kind === 'umb') {                               // a tall yellow tedung on its pole
    R(x + 3, hy - 9, 1, h + 4, '#4a3420'); R(x - 1, hy - 10, 9, 1, '#e8c040'); R(x, hy - 11, 7, 1, '#e8c040'); R(x + 2, hy - 12, 3, 1, '#f8e070');
    px(ctx, x + 3, hy - 13, '#f8e070'); R(x - 1, hy - 9, 1, 3, '#c09a30'); R(x + 7, hy - 9, 1, 3, '#c09a30'); px(ctx, x + 3, hy - 9, '#b08a28');
  }
  if (kind === 'fruit') {                             // a gebogan, a tower of fruit and cakes, on her head
    R(x - 1, hy - 1, 4, 1, '#c8a040'); R(x - 1, hy - 2, 4, 1, '#e84a3a'); R(x, hy - 3, 3, 1, '#f0c040'); R(x, hy - 4, 2, 1, '#6a9a3a');
    R(x, hy - 5, 2, 1, '#e86a8a'); px(ctx, x, hy - 6, '#e8e0a0');
  }
  if (kind === 'canang') { R(x + 3, hy + 5, 3, 1, '#d0dc8e'); px(ctx, x + 4, hy + 4, '#f0c040'); px(ctx, x + 3, hy + 4, '#e84a5a'); }
  if (kind === 'bell') px(ctx, x + 3, hy + 5 + (Math.floor(t * 4) & 1), '#e8c860');
}
const lg0 = h => h - 3 - Math.round(h * 0.34);
function drawProcession(t) {
  for (let k = PROC_CAST.length - 1; k >= 0; k--) {
    const p = procPos(k, t);
    if (!p) continue;
    ctx.globalAlpha = p[3]; procFigure(p[0], p[1], p[2], PROC_CAST[k], p[4], t); ctx.globalAlpha = 1;
  }
}
// people walking back across the reef from the temple, and a fisherman with a bamboo rod at the reef's edge
const REEFERS = [{ ph: 0, col: '#d8d4dc', rank: 'reefA' }, { ph: 40, col: '#3a6a9a', rank: 'reefB' }, { ph: 80, col: '#c8604a', rank: 'reefC' }];
function drawReefPeople(t, here) {
  for (const R0 of REEFERS) {
    if (!here(RANK[R0.rank])) continue;
    const tm = frac((t - R0.ph) / 120) * 120;
    if (tm > 64) continue;
    const [x, y] = along(PROC_PATH.slice().reverse(), tm / 64);
    ctx.globalAlpha = smooth(0, 3, tm); tiny(x, y, Math.round(lerp(11, 14, (y - 166) / 16)), R0.col, { step: Math.floor(t * 3.2) & 1 }); ctx.globalAlpha = 1;
  }
  if (here(RANK.fisher)) {                            // he casts every 20 s: the rod swings back, then out over the surf
    const { u } = cycle(t, 12, 0.3), sw = u < 0.08 ? -smooth(0, 0.08, u) : u < 0.14 ? -1 + 2 * smooth(0.08, 0.14, u) : 1 - smooth(0.14, 0.4, u) * 0.2;
    const fx = 44, fy = 177, hx = fx + 1, hy = fy - 8, ang = -0.95 - 0.5 * sw, L = 22;
    tiny(fx, fy, 13, '#6a5a3a', { bot: '#3a3040', hair: '#4a3020' });
    ctx.globalAlpha = 0.85;
    for (let k = 2; k <= L; k++) px(ctx, Math.round(hx + Math.cos(ang) * k), Math.round(hy + Math.sin(ang) * k), k < 8 ? '#6a5030' : '#8a7458');
    ctx.globalAlpha = 0.3;
    const tx = hx + Math.cos(ang) * L, ty = hy + Math.sin(ang) * L;
    for (let k = 1; k < 12; k++) px(ctx, Math.round(tx - k * 0.6), Math.round(ty + k * (0.9 + 0.3 * sw)), '#c8c8d8');
    ctx.globalAlpha = 1;
  }
}

/* ================= People near us, and what they do ================= */
const PEOPLE2 = {
  stA: look(24, 0, 1, '#d8a848', '#2a2a3a', { long: 5, lit: '#ffb880' }), stB: look(26, 2, 0, '#3a4a6a', '#4a4a54', { lit: '#ffb880' }),
  bagA: look(28, 1, 1, '#3a7a8a', '#3a3a48', { long: 6, lit: '#ffb880' }), bagB: look(29, 2, 0, '#8a5a3a', '#2a2a34', { lit: '#ffb880' }),
  reader: look(28, 0, 2, '#a8486a', '#2a2a3a', { long: 5, lit: '#ffb880' }),
  waiter: look(28, 2, 0, '#e8e4dc', '#2a2a30', { udeng: '#e8e4dc', sash: '#a83a2a', sarong: true, botL: '#4a4a52', dk: 0.2, lit: '#ffc890' }),
};
const KITE_HAND = [97, 188];
function drawPathPeople(t, here) {                    // the path in front of the wall, nearest last
  const DL = [], put = (fy, fn) => DL.push([fy, fn]);
  put(204.5, () => blit(LAMP_C));
  if (here(RANK.photo)) put(199, () => {              // a photo of the temple against the dusk, the phone up most of the time
    const up = cycle(t, 10, 0.2).u < 0.7 || SETS.some(S => t > S.burst - 2 && t < S.burst + 8);
    person(128, 199, PEOPLE.photo, up ? { aL: 1, aR: 1 } : {});
    if (up) { R(126, 171, 5, 3, '#1a1a24'); R(127, 172, 3, 1, '#d8ecff'); halo(HL.phone, 128, 172, 0.8); }
  });
  if (here(RANK.kid)) put(202, () => {                // the janggan's flyer, letting out line and taking it in
    const tug = cycle(t, 30, 0.1).u < 0.15;
    person(104, 202, PEOPLE.kid, { aL: tug ? 1 : 4 });
    const [hx, hy] = tug ? [101, 185] : KITE_HAND;
    for (let k = 0; k < 44; k++) { ctx.globalAlpha = 0.32 * (1 - k / 44); px(ctx, Math.round(hx + k * 0.25), hy - k, '#d8d0e8'); }
    ctx.globalAlpha = 1;
  });
  if (here(RANK.stroll)) for (const S of STROLL) {
    if (t < S.a || t >= S.b) continue;
    // from the warung's steps along the near side of the path and out of the frame, and back again
    const u = (t - S.a) / (S.b - S.a), x = Math.round(S.dir < 0 ? lerp(352, -18, u) : lerp(-18, 352, u)), fr = Math.floor(t * 3.2) & 1;
    const a = S.dir < 0 ? smooth(0, 0.04, u) : 1 - smooth(0.96, 1, u);
    put(210, () => { ctx.globalAlpha = a; person(x, 210, PEOPLE2.stB, { view: 2, dir: S.dir, step: fr }); ctx.globalAlpha = 1; });
    put(210.5, () => { ctx.globalAlpha = a; person(x + S.dir * -5, 211, PEOPLE2.stA, { view: 2, dir: S.dir, step: fr ^ 1 }); R(x + (S.dir < 0 ? 1 : -3), 201, 3, 1, PEOPLE2.stA.skin); ctx.globalAlpha = 1; });
  }
  if (here(0)) put(206, () => {                       // setting out the evening's offerings on the warung's steps
    const bend = cycle(t, 10, 0.4).u < 0.25;
    person(347, 206, PEOPLE.ibu, { view: 2, dir: 1, bend, aR: 3 });
  });
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
}
function drawWarungPeople(t, here) {                 // the keeper: splits a coconut at the heap, takes it down the counter
  const { u } = cycle(t, 6, 0.55), chop = u > 0.08 && u < 0.14;
  const x = Math.round(u < 0.2 ? 416 : u < 0.32 ? lerp(416, 456, smooth(0.2, 0.32, u)) : u < 0.5 ? 456 : u < 0.62 ? lerp(456, 438, smooth(0.5, 0.62, u)) : u < 0.9 ? 438 : lerp(438, 416, smooth(0.9, 1, u)));
  person(x, 192, PEOPLE.keeper, { view: 1, aL: chop ? 1 : 3, noShadow: true });
  if (chop) px(ctx, x - 5, 164, '#e8eef4');
}
function drawSachets(t) {                             // strips of snack sachets fluttering under the eave
  [['#e84a3a', '#f0c040'], ['#3a8ad8', '#f0f0f0'], ['#3aa860', '#f0c040'], ['#e87a2a', '#c83a6a']].forEach(([a, b], k) => {
    const sx = 368 + k * 4, top = WARUNG.eave + 5, len = 20 - (k & 1) * 4, sw = 1.2 * wave(t, 12 + k * 3, k * 0.27);
    px(ctx, sx, top, '#1a1210');
    for (let j = 1; j < len; j++) R(sx + Math.round(sw * j / len), top + j, 2, 1, Math.floor((j + 1) / 3) % 2 ? a : b);
  });
}
function drawFringe(t) {                              // the eave's grass ends stir in the breeze
  const s = step(t, 240), y0 = WARUNG.eave;
  for (let x = roofL(y0) - 3; x < W; x++) {
    const n = 2 + Math.floor(hash(x, 7, 105) * 3);
    if (hash(x, s, 241) < 0.35) { px(ctx, x, y0 + n, '#a07434'); px(ctx, x + 1, y0 + n, '#c8963e'); }
    else if (hash(x, s, 242) < 0.2) px(ctx, x, y0 + n + 1, '#8a6430');
  }
}
function drawWarungFront(t, here) {
  R(446, 184, 8, 2, '#c83a3a'); R(446, 186, 1, 6, '#8a2424'); R(453, 186, 1, 6, '#8a2424');   // a stool at the counter
  if (here(RANK.guest)) person(450, 184, PEOPLE.guest, { sit: true, aR: cycle(t, 12, 0.7).u < 0.15 ? 2 : 0 });
  if (cycle(t, 16, 0.2).u < 0.06) px(ctx, 467, 170, '#d8823a');                 // the cat's tail flicks
  for (const [ox, oy] of OFFERINGS) {                 // incense, a thread of smoke
    R(ox + 4, oy - 8, 1, 5, '#6a4a2a'); px(ctx, ox + 4, oy - 9, '#ff9a4a'); halo(HL.incense, ox + 4, oy - 9, 0.8 + 0.2 * wave(t, 48, ox / 40));
    ctx.globalAlpha = 0.35;
    for (let k = 1; k < 14; k++) px(ctx, ox + 4 + Math.round(1.4 * Math.sin(k * 0.6 + TAU * 24 * t / LOOP + ox)), oy - 9 - k, '#c8c0d0');
    ctx.globalAlpha = 1;
  }
}
// the lawn: a couple on two beanbags by a candle, someone on her phone, the waiter's trips, the dog
function drawLawn(t, here) {
  const DL = [], put = (fy, fn) => DL.push([fy, fn]);
  if (here(RANK.bags)) {
    const lean = cycle(t, 8, 0.3).u < 0.4, sipA = cycle(t, 8, 0.75).u < 0.1, sipB = cycle(t, 8, 0.15).u < 0.1;
    put(BEANBAGS[0].y, () => person(BEANBAGS[0].x + 1, BEANBAGS[0].y - 6, PEOPLE2.bagA, { sit: true, lean: lean ? 1 : 0, aR: sipA ? 2 : 0 }));
    put(BEANBAGS[1].y, () => person(BEANBAGS[1].x - 1, BEANBAGS[1].y - 6, PEOPLE2.bagB, { sit: true, lean: lean ? -1 : 0, aL: sipB ? 2 : 0 }));
  }
  if (here(RANK.reader)) put(BEANBAGS[2].y, () => {
    const b = BEANBAGS[2];
    person(b.x, b.y - 6, PEOPLE2.reader, { sit: true, aL: 2, aR: 2 });
    px(ctx, b.x + 3, b.y - 13, '#d8ecff'); halo(HL.phone, b.x + 3, b.y - 13, 0.7);
  });
  if (here(0)) for (const w0 of WAITER) {             // out along the stepping stones with coconuts, serve, back
    const tm = t - w0;
    if (tm < 0 || tm >= 22) continue;
    const out = tm < 9, back = tm >= 13, s = out ? tm / 9 : back ? 1 - (tm - 13) / 9 : 1;
    const [x, y] = along(STONES.map(([sx, sy]) => [sx, sy]), s), fr = (out || back) ? Math.floor(t * 3.2) & 1 : 0;
    put(y, () => {
      const P = PEOPLE2.waiter;
      person(x, y, P, { view: out || back ? 2 : 0, dir: out ? -1 : 1, step: fr, aR: out ? 3 : tm < 13 ? 3 : 0 });
      if (tm < 11) { const tx = Math.round(x) + (out ? -6 : 3), ty = Math.round(y) - P.ll - P.th + 2; R(tx, ty, 5, 1, '#8a6a4a'); R(tx, ty - 3, 2, 3, '#5a8a30'); R(tx + 3, ty - 3, 2, 3, '#5a8a30'); }
    });
  }
  put(250, () => {                                    // the warung's dog, asleep on the lawn; once a loop it gets up and circles
    const up = t >= DOG_UP[0] && t < DOG_UP[1] - 2, turned = t >= DOG_UP[0] + 2 && t < DOG_UP[0] + 6;
    if (up) ctx.drawImage(DOG_UP_C[turned ? 1 : 0], 220, 241); else ctx.drawImage(DOG_SPR, 220, 245);
  });
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
}
const FLIES = [];
{ const r = rng(171); for (let i = 0; i < 24; i++) FLIES.push({ bx: 40 + r() * 430, by: 192 + r() * 66, amp: 10 + r() * 16, k1: 2 + Math.floor(r() * 5), k2: 3 + Math.floor(r() * 5), p1: r(), p2: r(), kb: 40 + Math.floor(r() * 40), pb: r() }); }
function drawFlies(t) {                               // fireflies over the lawn and the frangipani
  for (const f of FLIES) {
    const x = f.bx + f.amp * wave(t, f.k1, f.p1), y = f.by + f.amp * 0.5 * wave(t, f.k2, f.p2), b = Math.max(0, wave(t, f.kb, f.pb)) ** 3;
    halo(HL.fly, x, y, 0.15 + 0.85 * b);
    ctx.globalAlpha = 0.3 + 0.7 * b; px(ctx, Math.round(x), Math.round(y), b > 0.4 ? '#f4ffb0' : '#a8d060'); ctx.globalAlpha = 1;
  }
}
function drawMoths(t) {                               // two moths round the path lamp
  for (let i = 0; i < 2; i++) {
    const a = TAU * ((60 + i * 17) * t / LOOP + i * 0.4), x = LAMP.x + Math.cos(a) * (6 + i * 3), y = 160 + Math.sin(a * 1.3) * (5 + i * 2);
    px(ctx, Math.round(x), Math.round(y), '#fff2dc'); halo(HL.moth, x, y, 0.7);
  }
}
// frangipani blossoms on the breeze (the city's own weather): a few far off from the temple's trees, more falling
// from ours across the path, and near ones tumbling past us
const PETALS = [
  { n0: 12, kMin: 6, kMax: 9, size: 1, x: [170, 330], y: [70, 168], drift: -26 },
  { n0: 16, kMin: 8, kMax: 12, size: 2, x: [296, 400], y: [118, 212], drift: -46 },
  { n0: 8, kMin: 10, kMax: 14, size: 3, x: [300, 470], y: [100, 272], drift: -110 },
].map((L, li) => { const r = rng(181 + li); return { ...L, list: Array.from({ length: L.n0 }, (_, i) => ({ id: i + li * 100, n: L.kMin + Math.floor(r() * (L.kMax - L.kMin + 1)), ph: r(), k: 20 + Math.floor(r() * 30), p: r() })) }; });
function drawPetals(t, li) {
  const L = PETALS[li];
  for (const q of L.list) {
    const { cyc, u } = cycle(t, q.n, q.ph);
    const x = lerp(L.x[0], L.x[1], hash(q.id, cyc, 182)) + L.drift * u + 4 * wave(t, q.k, q.p), y = lerp(L.y[0], L.y[1], u);
    ctx.globalAlpha = Math.min(1, u * 8, (1 - u) * 8);
    const X = Math.round(x), Y = Math.round(y);
    if (L.size === 1) px(ctx, X, Y, '#e8e4d8');
    else if (L.size === 2) { R(X, Y, 2, 1, '#f4f0e4'); px(ctx, X + (wave(t, q.k, q.p) > 0 ? 1 : 0), Y + 1, '#f0c848'); }
    else { R(X - 1, Y, 3, 1, '#faf6ec'); R(X, Y - 1, 1, 3, '#faf6ec'); px(ctx, X, Y, '#f2c848'); px(ctx, X + 1, Y + 1, '#d8d0c4'); }
  }
  ctx.globalAlpha = 1;
}

/* ================= What the breeze and the water do ================= */
// water light: the floods' glare off the moving sheet on the reef dances on the rock's lit foot (12 frames round a
// circle in noise space, so the cycle closes)
const CAUSTIC_N = 12;
function renderCaustics(f) {
  const p = painter(TL.x1 - TL.x0 + 9, 44, TL.x0 - 4, 120), a = TAU * f / CAUSTIC_N;
  for (let y = 120; y < TL.base - 1; y++) for (let x = TL.x0 - 4; x <= TL.x1 + 4; x++) {
    if (!inRock(x, y)) continue;
    let pool = 0;
    for (const [fx, fy, rr] of FLOODS) pool = Math.max(pool, 1 - Math.hypot((x - fx) / rr, (y - fy) / (rr * 1.25)));
    if (pool < 0.25 || CAVES.some(c => Math.abs(x - c.cx) < c.w / 2 + 1 && y > TL.base - c.h - 1)) continue;
    const n = vnoise(x / 5 + Math.cos(a) * 0.9, y / 2.2 + Math.sin(a) * 0.9, 201), ridge = 1 - Math.abs(2 * n - 1);
    if (ridge > 0.9) p.put(x, y, '#ffd080', Math.round(255 * Math.min(1, (pool - 0.25) * 1.6) * 0.32 * smooth(126, 150, y)));
  }
  return p.done();
}
function renderRustle(k) {                            // a sprinkle of the crowns' leaves a shade lighter
  const p = painter(W, 150, 0, 40);
  for (const [x, y, col] of LEAF_PX) if (hash(x, y * 7 + k, 202) < 0.45) p.put(x, y, col);
  return p.done();
}
const SEA_GLINTS = [];
{ const r = rng(211); for (let i = 0; i < 46; i++) SEA_GLINTS.push({ id: i, x: Math.floor(r() * W), y: HZ + 2 + Math.floor(r() ** 1.4 * 26), w: 2 + Math.floor(r() * 4), n: 24 + Math.floor(r() * 30), ph: r() }); }
function drawSeaGlints(t) {
  for (const g of SEA_GLINTS) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.5) continue;
    const x = g.x + Math.round((hash(g.id, cyc, 212) - 0.5) * 24), warm = Math.max(0, 1 - Math.abs(x - GLOW) / 120);
    if (x > reefEdge(x) && g.y > reefEdge(x) - 2) continue;
    ctx.globalAlpha = Math.sin(Math.PI * u * 2) * (0.25 + 0.4 * warm);
    R(x, g.y, g.w, 1, warm > 0.3 ? '#ffc88a' : '#a8b0e0');
  }
  ctx.globalAlpha = 1;
}
function drawWovenLamps(t) {                          // the warung's woven lamps sway a little on their wires in the breeze
  W_LAMPS.forEach(([lx, ly], i) => {
    const d = Math.round(1.2 * wave(t, 16 + i * 4, i * 0.3)), top = WARUNG.eave + 3;
    for (let y = top; y < ly - 3; y++) px(ctx, lx + Math.round(d * (y - top) / (ly - 3 - top)), y, '#1a1210');
    const x = lx + d;
    R(x - 2, ly - 3, 5, 1, '#8a5a2e'); R(x - 3, ly - 2, 7, 2, '#c8904a'); px(ctx, x - 3, ly - 1, '#e8b468'); R(x - 1, ly, 3, 1, '#fff4d0');
    halo(HL.wlamp, x, ly, 0.95);
  });
}

// incense from the temple's shrines: two thin plumes rising behind the three-tiered meru, drifting inland on the breeze
const PLUMES = [{ x: 228, k: 0 }, { x: 244, k: 1 }];
const SMOKE = [];
{ const r = rng(221); for (let i = 0; i < 26; i++) SMOKE.push({ n: 20 + Math.floor(r() * 8), ph: r(), k: 14 + Math.floor(r() * 14), p: r(), pl: i % 2 }); }
function drawTempleSmoke(t) {
  for (const s of SMOKE) {
    const { u } = cycle(t, s.n, s.ph), P = PLUMES[s.pl], y0 = dome(P.x) - 8;
    const x = P.x + 16 * u * u + 3 * u + 2 * wave(t, s.k, s.p) * u, y = y0 - 50 * u;
    const r = Math.min(4, 1 + Math.floor(u * 5));
    ctx.globalAlpha = 0.18 * Math.min(1, u * 10) * (1 - u);
    ctx.drawImage(PUFF[r], Math.round(x) - r, Math.round(y) - r);
  }
  ctx.globalAlpha = 1;
}
function makePuff(r, light = '#b8b0c8', dark = '#8a86a4') {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done().c;
}
// swiftlets wheeling round the island's sea caves at dusk
const SWIFTS = [];
{ const r = rng(231); for (let i = 0; i < 5; i++) SWIFTS.push({ cx: 140 + r() * 150, cy: 112 + r() * 26, rx: 30 + r() * 40, ry: 10 + r() * 12, k: 9 + Math.floor(r() * 6), k2: 2 + Math.floor(r() * 3), ph: r() }); }
function drawSwifts(t) {
  for (const b of SWIFTS) {
    const a = TAU * (b.k * t / LOOP + b.ph), x = Math.round(b.cx + b.rx * Math.cos(a)), y = Math.round(b.cy + b.ry * Math.sin(a) + 4 * wave(t, b.k2, b.ph));
    const up = (Math.floor(t * 8) + Math.round(b.ph * 7)) & 1;
    R(x, y, 1, 1, '#0e0a12'); R(x - 2, y - up, 2, 1, '#0e0a12'); R(x + 1, y - up, 2, 1, '#0e0a12');
  }
}
const COAST_LIGHTS = [[338, HZ - 3], [352, HZ - 4], [371, HZ - 6], [396, HZ - 8], [412, HZ - 9], [444, HZ - 9], [462, HZ - 9]];
function drawCoastLights(t) {
  COAST_LIGHTS.forEach(([x, y], i) => { ctx.globalAlpha = 0.5 + 0.5 * wave(t, 24 + i * 7, i * 0.17); px(ctx, x, y, '#ffd890'); });
  ctx.globalAlpha = 1;
}

/* ================= The weather a visitor picks ================= */
// Bali's own weather is frangipani blossoms on the breeze (no rain at this time of the dry season). A pick falls far
// onto the sea, mid onto the reef and the wall, near past the bottom of the frame, catches the lamps, and stays out
// from under the warung's roof.
const WEATHER = weatherPlans(['petals']);
const WX_LIGHTS = [
  { x: LAMP.x, y: 160, rx: 24, ry: 34, col: '#ffc070' }, ...POSTS.map(x => ({ x, y: 175, rx: 10, ry: 12, col: '#ffc070' })),
  { x: 420, y: 166, rx: 64, ry: 30, col: '#ffb860' }, ...W_LAMPS.map(([x, y]) => ({ x, y, rx: 16, ry: 14, col: '#ffcf80' })),
  ...CANDLES.map(([x, y]) => ({ x, y: y - 4, rx: 10, ry: 8, col: '#ffb860' })), { x: SPRING.x, y: 152, rx: 12, ry: 10, col: '#ffb050' },
  ...FLOODS.map(([x]) => ({ x, y: 150, rx: 26, ry: 22, col: '#ffc070' })),
];
const WX = { land: [[HZ + 4, 160], [164, 196], [H + 4, H + 4]], lights: WX_LIGHTS, shelter: [[], [], [[364, 142, 476, 191]]] };
const WX_BB = { ...WX, lights: [...WX_LIGHTS, ...BB_LAMPS.map(x => ({ x, y: BB.y + 4, rx: 14, ry: 10, col: '#ffd090' }))] };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUD_C, COAST_C, SEA_C, REEF_C, ISLAND_C, KITE_BEB, KITE_JAN, JUK_C, PITCH, GARDEN_C, PLANTS_C, WALL_C,
  PATH_C, PALM_T, PALM_F, LAMP_C, CLOUD2_C, CAUSTIC_C, RUSTLE_C, PLANT_F, PUFF, SPRAY, CAT_WALK, WARUNG_C, FRANGI_C, PENJOR_C, SAMP_C, SCOOT_C, BB_C, BB_AD, DOG_SPR, DOG_UP_C, VIG_C;
const DOG_STAND = ['.........ee', '........eTe', '.ttttttttTT', 'tTTTTTTTTT.', '.T.T....T.T', '.K.K....K.K'];
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.put(i, y, '#060412', Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(52, '#8a90d8', 0.16, 1.7), moonCore: makeHalo(18, '#fff0d0', 0.3, 1.5), glow: makeHalo(120, '#ff9a5a', 0.12, 1.4),
    rock: makeHalo(96, '#ffa24a', 0.1, 1.5), rockCore: makeHalo(40, '#ffc070', 0.12, 1.4), flood: makeHalo(10, '#ffe0a0', 0.4, 1.3),
    lamp: makeHalo(5, '#ffd890', 0.45, 1.2), spring: makeHalo(18, '#ffb050', 0.3, 1.4), tlamp: makeHalo(10, '#ffc870', 0.42, 1.5), tlampCore: makeHalo(3, '#fff0c0', 0.6, 1),
    mist: makeHalo(36, '#d8e8f0', 0.16, 1.4), fly: makeHalo(4, '#c8ff6a', 0.32, 1.1), moth: makeHalo(2, '#fff0c0', 0.4, 1),
    lampL: makeHalo(58, '#ffb060', 0.24, 1.7), lampCore: makeHalo(14, '#ffe6b0', 0.5, 1.4), lantern: makeHalo(12, '#ffc070', 0.32, 1.4),
    bulb: makeHalo(8, '#ffd890', 0.34, 1.4), wlamp: makeHalo(36, '#ffaa50', 0.22, 1.6), warung: makeHalo(80, '#ff9a48', 0.1, 1.5),
    phone: makeHalo(5, '#c8e8ff', 0.5, 1.2), incense: makeHalo(2, '#ff8a3a', 0.6, 1), candle: makeHalo(9, '#ffb060', 0.4, 1.4),
    bbLamp: makeHalo(5, '#ffd090', 0.32, 1.3), bbWash: makeHalo(30, '#ffc080', 0.07, 1.6),
  };
  FRANGI_C = renderFrangipani();                      // (first: it adds to LEAF_PX)
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUD_C = renderClouds(); CLOUD2_C = renderClouds(CLOUD_BANKS2); COAST_C = renderCoast();
  SEA_C = renderSea(); REEF_C = renderReef(); ISLAND_C = renderIsland();
  KITE_BEB = sprite(BEBEAN_ROWS, KITE_C); KITE_JAN = sprite(JANGGAN_ROWS, KITE_C); JUK_C = sprite(JUKUNG_ROWS, JUKUNG_C);
  PITCH = makePitch(BB.w, BB.h, { accent: '#1fb5cc' });
  WALL_C = renderWall(); PATH_C = renderPath(); GARDEN_C = renderGarden(); PLANT_F = [-1, -0.5, 0, 0.5, 1].map(renderPlants); LAMP_C = renderLamp();
  CAUSTIC_C = Array.from({ length: CAUSTIC_N }, (_, f) => renderCaustics(f)); RUSTLE_C = Array.from({ length: 6 }, (_, k) => renderRustle(k));
  PALM_T = renderPalmTrunk(); PALM_F = Array.from({ length: 7 }, (_, f) => renderPalmCrown(f / 6 * 2 - 1));
  WARUNG_C = renderWarung(); PENJOR_C = [-1, -0.5, 0, 0.5, 1].map(renderPenjor); SCOOT_C = sprite(SCOOTER, SCOOTER_C);
  SAMP_C = sprite(SAMPIAN, { Y: '#ece2a4', y: '#a8a464' });
  BB_C = renderBillboard(); BB_AD = renderAd(); DOG_SPR = sprite(DOG, DOG_C); DOG_UP_C = [sprite(DOG_STAND, DOG_C), sprite(DOG_STAND, DOG_C, true)];
  PUFF = [null, 1, 2, 3, 4].map(r => r && makePuff(r));
  SPRAY = [null, 1, 2, 3, 4, 5, 6].map(r => r && makePuff(r, '#f6fbfb', '#b4c8d6'));
  const CW = [['t.......', '.t....kk', '.tGGGGGk', '..GGGGG.', '..g..g..', '.g....g.'], ['t.......', 't.....kk', '.tGGGGGk', '..GGGGG.', '...gg...', '..g..g..']];
  CAT_WALK = [false, true].map(fl => CW.map(rows => sprite(rows, { t: '#3a3036', k: '#2a2228', G: '#2e262c', g: '#1e181c' }, fl)));
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
function drawSky(t) {
  blit(SKY_C);
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    if (s.big) { ctx.globalAlpha = a * 0.3; rect(ctx, s.x - 1, s.y, 3, 1, s.col); rect(ctx, s.x, s.y - 1, 1, 3, s.col); }
    ctx.globalAlpha = a; px(ctx, s.x, s.y, s.col);
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.15 * wave(t, 4)); halo(HL.moonCore, MOON.x, MOON.y);   // the moon's haze breathes with the passing cloud
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  drawClouds(t);
  halo(HL.glow, GLOW, HZ + 4, 0.86 + 0.08 * wave(t, 2, 0.25));   // the afterglow settles and lifts
  drawFoxes(t);
  drawKites(t);
  blit(COAST_C);
  drawCoastLights(t);
}
function drawSea(t) {
  blit(SEA_C);
  drawSwells(t);
  streak(ctx, t, GLOW, 18, '#ffb070', 0.32, 3, HZ + 1, 176);               // the afterglow on the water
  streak(ctx, t, GLOW, 7, '#ffe0a0', 0.4, 5, HZ + 1, 170);
  streak(ctx, t, MOON.x, 5, '#e0dcf0', 0.22, 7, HZ + 1, 150);              // the moon's path
  drawSeaGlints(t);
  drawJukung(t);
  blit(REEF_C);
  drawSurf(t);
}
function drawIsland(t) {
  halo(HL.rock, TL.cx, 136, 0.9);
  blit(ISLAND_C);
  ctx.globalCompositeOperation = 'lighter'; blit(CAUSTIC_C[step(t, 720) % CAUSTIC_N]); ctx.globalCompositeOperation = 'source-over';
  { const r = (Math.sin(TAU * 18 * t / LOOP) + 1) / 2; ctx.globalAlpha = 0.5 + 0.5 * r; blit(RUSTLE_C[step(t, 360) % 6]); ctx.globalAlpha = 1; }   // the breeze in the crowns
  drawTempleLamps(t);
  drawTempleSmoke(t);
  drawSwifts(t);
  halo(HL.rockCore, TL.cx, 150, 0.8);
  reflect(ctx, t, { top: TL.base + 1, rows: 14, squash: 1.4, alpha: 0.66, amp: 0.4, grow: 0.04, x: TL.x0, w: TL.x1 - TL.x0 + 1 });
  for (const [fx] of FLOODS) streak(ctx, t, fx, 14, '#ffc070', 0.3, fx, TL.base + 2, barTop(fx));   // the rock's light on the wet reef
  streak(ctx, t, SPRING.x, 6, '#ffb050', 0.34, 9, TL.base + 2, barTop(SPRING.x) + 1);
  drawRipples(t);
  const { cyc, u } = cycle(t, BREAKERS);              // each breaker washes round the island's feet
  if (u > 0.58 && u < 0.95) {
    const f = 1 - (u - 0.58) / 0.37;
    for (const [x0, x1] of [[78, 104], [320, 338]]) for (let x = x0; x <= x1; x++) {
      if (hash(x, cyc, 191) > 0.6) continue;
      ctx.globalAlpha = 0.8 * f; px(ctx, x, 161 + Math.round(hash(x, cyc, 192) * 3 - (u - 0.58) * 6), '#e8f4f4');
    }
    ctx.globalAlpha = 1;
  }
  for (const [fx, fy] of FLOODS) { rect(ctx, fx - 1, fy - 6, 3, 1, '#2a1a14'); px(ctx, fx, fy - 7, '#fff0c8'); halo(HL.flood, fx, fy - 7, 0.9); }
  halo(HL.spring, SPRING.x, 154, 0.85 + 0.15 * wave(t, 36));
  drawSpray(t);
  drawFlood(t);
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.9);
  blit(BB_C);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 2, 3, 1, '#2a1a14'); px(ctx, lx, BB.y - 1, '#fff0c8'); halo(HL.bbLamp, lx, BB.y - 1, 0.9); }
}
function drawNear(t, { pitch, billboard, here, wx, wxc, reducedMotion }) {
  blit(WALL_C);
  if (here(RANK.couple)) {                            // a couple on the wall, watching; she points when a set bursts
    const point = SETS.some(S => t > S.burst - 1 && t < S.burst + 7), lean = cycle(t, 8, 0.6).u < 0.45;
    person(201, WALL, PEOPLE.she, { sit: true, lean: lean ? 1 : 0, aL: point ? 4 : 0 });
    person(208, WALL, PEOPLE.he, { sit: true, aL: 5, lean: lean ? -1 : 0 });
  }
  for (const C of WALL_CAT) {                        // a cat walks along the wall's cap, tail up
    if (t < C.a || t >= C.b) continue;
    const u = (t - C.a) / (C.b - C.a), x = Math.round(C.dir > 0 ? lerp(-12, 352, u) : lerp(352, -12, u)), fr = Math.floor(t * 4) & 1;
    if (x > 190 && x < 220 && here(RANK.couple)) continue;                    // (it hops down past the couple)
    ctx.drawImage(CAT_WALK[C.dir > 0 ? 0 : 1][fr], x, WALL - 6);
  }
  blit(PATH_C); blit(GARDEN_C);
  blit(FRANGI_C);
  ctx.drawImage(SCOOT_C, SCOOT.x, SCOOT.y);
  drawPathPeople(t, here);
  blit(WARUNG_C.back);
  drawSachets(t); drawFringe(t);
  drawWovenLamps(t);
  if (here(0)) drawWarungPeople(t, here);
  blit(WARUNG_C.front);
  if (billboard) drawBillboard(t, pitch);
  drawWarungFront(t, here);
  drawLawn(t, here);
  const pj = 0.8 * wave(t, 8, 0.1) + 0.2 * wave(t, 21), pf = Math.round((pj + 1) * 2);   // the penjor bends in the breeze
  blit(PENJOR_C[pf]);
  ctx.drawImage(SAMP_C, PENJOR.tip[0] - 3 + (pf - 2) * 1 + Math.round(1.4 * wave(t, 20)), PENJOR.tip[1] + 2 - Math.abs(pf - 2) / 2 | 0);   // the sampian swings
  blit(PALM_T);
  blit(PALM_F[Math.round((0.7 * wave(t, 10) + 0.3 * wave(t, 23, 0.4) + 1) / 2 * 6)]);
  blit(PLANT_F[Math.round((wave(t, 12, 0.2) * 0.7 + wave(t, 31) * 0.3 + 1) / 2 * 4)]);
  if (wx.petals) drawPetals(t, 1);
  drawFlies(t); drawMoths(t);
  halo(HL.lampL, LAMP.x, 160, 0.95); halo(HL.lampCore, LAMP.x, 160, 1);
  for (const x0 of POSTS) halo(HL.lantern, x0, 175, 0.85 + 0.1 * wave(t, 90, x0 / 100));
  halo(HL.warung, 420, 170, 1);
  for (const [bx, by] of EAVE_BULBS) { px(ctx, bx, by, '#fff0c0'); halo(HL.bulb, bx, by, 0.7 + 0.3 * wave(t, 40, bx / 60)); }
  CANDLES.forEach(([cx, cy], i) => halo(HL.candle, cx, cy - 3, 0.82 + 0.18 * hash(i, step(t, 960), 193)));
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  if (wx.petals) drawPetals(t, 2);
  blit(VIG_C);
}
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves the sign out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the people
// to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  const here = rank => crowd > 0 && (rank === 0 || rank < crowd);
  drawSky(t);
  drawSea(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawIsland(t);
  if (crowd > 0) drawProcession(t);
  drawReefPeople(t, here);
  if (wx.petals) drawPetals(t, 0);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawNear(t, { pitch, billboard, here, wx, wxc, reducedMotion });
}

export default {
  id: 'bali',
  name: 'Bali',
  tagline: 'Blue hour at Tanah Lot',
  timeZone: 'Asia/Makassar',
  accent: '#1fb5cc',                                   // the Indian Ocean at dusk, written out: tools/seo/seo.mjs reads it from the source
  focusX: 167,                                         // the meru
  landmark: [86, 328],                                 // Tanah Lot's rock, end to end
  landmarkTop: 35,                                     // the meru's finial
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: 149, y: 35, w: 37, h: 44 },  // the five-tiered meru
    { kind: 'water', x: 130, y: 166, w: 150, h: 16 },   // the reef's sheet of water in front of the island
    { kind: 'window', x: 392, y: 146, w: 12, h: 10 },   // the warung's woven lamp
  ],
  ambience: { waves: 0.75, wind: 0.2, crickets: 0.32, crowd: 0.08 },
  events: [
    ...SETS.flatMap(S => [{ t: S.burst - 6, type: 'splash', x: 80, gain: 0.22 }, { t: S.burst, type: 'splash', x: 100, gain: 0.6 }]),
    { t: PROC.a + 2, type: 'bell', x: 400, gain: 0.16 }, { t: PROC.a + PROC.walk + 1, type: 'bell', x: SPRING.x, gain: 0.26 },
    { t: PROC.a + PROC.walk + PROC.rest + PROC.climb, type: 'bell', x: 320, gain: 0.2 },
    ...TLAMPS.slice(0, 3).map(L => ({ t: L.on + 0.5, type: 'chime', x: L.x, gain: 0.14 })),
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
